import { PartnerAbrechnung, zeigtPartnerBank } from "./produktpartner/PartnerAbrechnung";
import { PartnerVertragskarten, type PartnerVertragskartenRef } from "./produktpartner/PartnerVertragskarten";
import { PartnerAnsprechpartnerListe, type PartnerAnsprechpartnerListeRef } from "./produktpartner/PartnerAnsprechpartnerListe";
import { isPartnerStatus } from "./redesign/contactTabs";
import * as React from "react";
import { kontoinhaberVorschlag } from "../utils/kontoinhaber";
import { plzPruefen, plzSperrtSpeichern } from "./forms/validation/laenderprofil";
import type { BankTreffer } from "./forms/widgets/bankleitzahlen";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { REFINED } from "./risikoanalyseStep/tokens";
import {
  SectionCard,
  FieldRow,
  LabeledInput,
  LabeledTextarea,
  LabeledSelect,
  useFormPrimitiveStyles,
  useDuplicateCheck,
  DuplicateWarningPanel,
  RequiredFieldsProgress,
  IconGlobe,
  IconVisitenkarte,
  PhoneInput,
  EmailInput,
  IbanInput,
  BicInput,
  UstIdNrInput,
  SteuernummerInput,
  LegalFormSelect,
  FinanzamtSelect,
  HandelsregisterInput,
  AddressAutocomplete,
  KartenKnopf,
  CountrySelect,
  TagInput,
  SocialMediaInput,
  PLACEHOLDERS,
  validatePhone,
  validateEmail,
  validateIban,
  validateBic,
  validateUstIdNr,
  validateSteuernummer,
} from "./forms";
import { formatGruppeMitFreitext } from "../data/brancheGruppen";
import {
  BrancheGruppenListe,
  type BrancheGruppenEintrag,
} from "./forms/widgets/BrancheGruppenListe";
import { OptionChips } from "./forms/widgets/OptionChips";
import { UNTERNEHMENSFUNKTIONEN, istBesitzgesellschaft } from "../data/unternehmensfunktion";
import OwnershipSection from "./forms/OwnershipSection";
import { ConvertBanner } from "./ConvertBanner";
import { HerkunftAbschnitt } from "./herkunft/HerkunftAbschnitt";
import { herkunftVerknuepfen } from "./herkunft/herkunftBezug";
import { useContactOrigins } from "./useContactOrigins";
import { CompanyRelationsSection } from "./relationsInline/CompanyRelationsSection";
import {
  ownershipAusFormular,
  persistCollectedRelations,
  syncCollectedRelations,
  assignKundenverbindung,
  describeRemovedRelations,
} from "./relationsInline/persistRelations";
import { ConfirmDialog } from "./redesign/ConfirmDialog";
import FirmendatenBefuellenDialog, { type LogoFrage } from "./FirmendatenBefuellenDialog";
import { befuellenMoeglich } from "./firmendatenBefuellenFreigabe";
import { logoUebernehmen } from "../services/firmendatenService";
import { datenUrlAlsDatei } from "../utils/logoVorschlag";
import {
  WeitereKommunikationDialog,
  type WeitereKommunikationEintrag,
} from "./forms/widgets/WeitereKommunikationDialog";
import {
  ErreichbarkeitModal,
  type ErreichbarkeitRufnummerItem,
} from "./forms/widgets/ErreichbarkeitModal";

import {
  VereinMitgliedschaften,
  type VereinEintrag,
} from "./forms/widgets/VereinMitgliedschaften";
import { menschEintragen } from "../utils/visitenkarteZuFormState";
import DateiScannenDialog, {
  type DateiScanUebernahme,
} from "./DateiScannenDialog";
import { vorhandeneAnlageWerte } from "./firmendatenVorauswahl";
import { HandelsregisterKnoepfe } from "./HandelsregisterKnoepfe";
import {
  gesellschaftsvertragAuswerten,
  registerBeteiligteUebernehmen,
  registerAbdruckAbrufen,
  type UebernahmeEintrag,
} from "../services/firmendatenService";
import { documentService } from "../services/documentService";
import type { AnlageNachlaufRef } from "../utils/anlageNachlauf";
import { GruendungsdokumentFeld } from "./forms/widgets/GruendungsdokumentFeld";
import { buildMenschenItems } from "./verantwortlichkeit/buildMenschenItems";
import { attachMenschen, createNkgVorgang } from "../services/menschenFuersUnternehmenService";
import { branchengruppenSpeichern } from "../services/branchengruppeService";
import { companyRequiredChecks } from "./newCompanyRequired";
import { AnlageSpeichernLeiste, type AnlageMangel } from "./forms/AnlageSpeichernLeiste";
import { vereineAlsVerbindungen } from "../utils/bearbeitenBeziehungen";
import {
  OffeneVollanlagenContext,
  VollanlageElternContext,
  speichernWegenVollanlageBlockiert,
  useOffeneVollanlagenRegister,
  type VollanlageEltern,
} from "./forms/VollanlageWeiter";
import { NkgKarte } from "./forms/NkgKarte";
import { ZuwendungenKarte } from "./forms/ZuwendungenKarte";
import { useNavigate } from "react-router";
import { STATUS_PRODUKTPARTNER } from "../data/produktpartnerKatalog";
import { bezuegeAusFeld } from "./forms/widgets/StatusBezuege";
import { useAuth } from "../context/AuthContext";
import {
  STATUS_SONSTIGE,
  rollenAusFeld,
} from "../data/kontaktstatusSegment";
import { GesellschaftPicker } from "./produktpartner/GesellschaftPicker";
import type { GesellschaftDetail } from "../services/produktpartnerService";
import { apiUrl } from "../utils/api";
import {
  fetchLegalFormOptions,
  rechtsformAusName,
  type LegalFormOption,
} from "../utils/legalFormOptions";
import { RECHTSFORM_BEZEICHNUNG } from "@kmvs/forms-core";
import {
  rechtsformOptionAus,
  unternehmensBegriffAus,
  useUnternehmensBegriff,
  verantwortungTitel,
} from "../utils/unternehmensBegriff";
import { EintragsListe } from "./forms/EintragsListe";
import {
  VollmachtenKarte,
  anschriftEinzeilig,
  standardVollmachtAuswahl,
  vollmachtenNachAnlageMelden,
  type VollmachtAuswahl,
} from "./vollmachten/VollmachtenKarte";
import { verbundAusFormular } from "./vollmachten/erstinfoSammel";
import type { FirmaConnItem } from "./relationsInline/types";
import { neueWegId, wegFormatOk, wegeLesen } from "./forms/kommunikation/weitereWege";
import { KartenKlappContext, useKartenKlapp } from "./forms/kartenKlapp";
import { firmaKarten, leereKarten } from "./forms/kartenLeer";
import {
  WeitereAnschriftFelder,
  WeitereBankFelder,
  anschriftIstLeer,
  anschriftPruefen,
  bankIstLeer,
  bankPruefen,
  neueAnschrift,
  neueBank,
  type WeitereAnschrift,
  type WeitereBank,
} from "./forms/weitereEintraegeFelder";
import { anschriftKurz, anzahlKurz, bankKurz, kommunikationKurz } from "./forms/kartenKurzfassung";
import { KOERPERSCHAFT_FIRMA, fuehrtGemeinnuetzigkeit, koerperschaftTexte } from "../utils/koerperschaftBegriff";
import { AoZweckeFeld } from "./redesign/AoZweckeFeld";
import { KontaktStatusSchritt } from "./forms/KontaktStatusSchritt";
import { GegenueberKarte } from "./forms/widgets/GegenueberKasten";
import { folgeFrage } from "../data/gegenueberFolgen";
import type { FormState } from "../utils/formUtils";
// Geschäftsjahr: Monatsnamen, Tage-pro-Monat und Tages-Optionen kommen aus
// `utils/geschaeftsjahr` — dieselbe Quelle wie in der Detailansicht, damit ein
// Stichtag überall gleich gelesen und geschrieben wird. Die Konstanten standen
// vorher doppelt in beiden Dateien.
import { GJ_MONTHS, gjDaysInMonth, gjTagOptionen } from "../utils/geschaeftsjahr";
import { ortsteilLeeren } from "../utils/ortsteilAbgleich";
import { hinweisen } from "./redesign/bestaetigen";
import {
  KommHinweise,
  andererKontaktKommHinweis,
  doppeltKommHinweis,
  erreichbarkeitUmhaengen,
  telefonMobilKommHinweis,
  useTelefonMobilPaar,
} from "./forms/kommunikation/TelefonMobilHinweis";
import { doppelteWege, weitereKommWege, type KontaktWeg, type WegTyp } from "./forms/kommunikation/doppelteWege";
import { useNummernAndererKontakte } from "./forms/kommunikation/useNummernAndererKontakte";
import { elementMitId } from "./windowManager/browserFenster/fensterRegister";

/**
 * Saubere, neu aufgebaute Firmenkontakt-Anlage auf C0/BRAND-Bausteinen.
 * Ersetzt die alte SinglePageCompanyForm im Create-Flow. Bindet an dieselben
 * formFields-Keys (Persistenz/RBAC unverändert) und reicht den Save-Flow an die
 * Page durch (onSave = build payload + POST + Menschen/Adresse/Tel → id).
 */

const useStyles = makeStyles({
  page: {
    minHeight: "100%",
    backgroundColor: REFINED.white,
    fontFamily: REFINED.fontUI,
    color: REFINED.ink,
    display: "flex",
    flexDirection: "column",
  },
  scroll: { flex: 1, overflowY: "auto" },
  inner: {
    // Volle Breite (User 30.09.2026): die Karten füllen den verfügbaren Platz.
    // Ohne border-box käme der Innenabstand zur Breite dazu und die Seite
    // ließe sich seitlich schieben (User 26.09.2026).
    width: "100%",
    boxSizing: "border-box",
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  // In der Akte eingebettet: die Akte (`.dbody`) bringt schon 32 px Rand mit —
  // seitlich kein zweiter Innenabstand.
  innerEingebettet: { paddingLeft: 0, paddingRight: 0 },
  pageTitle: { fontFamily: REFINED.fontUI, fontSize: "1.7rem", fontWeight: 700, color: REFINED.ink, textAlign: "center", margin: "12px 0 24px" },
  radioRow: { display: "flex", flexWrap: "wrap", gap: "14px", paddingTop: "4px" },
  radioItem: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.92rem",
    color: REFINED.ink,
    cursor: "pointer",
    "& input": { accentColor: REFINED.aub, cursor: "pointer" },
  },
  rowActions: { display: "flex", justifyContent: "flex-end", marginTop: "2px" },
  ghostBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "9px 16px",
    cursor: "pointer",
    transitionProperty: "background-color, color",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  removeBtn: {
    ...shorthands.border("1px", "solid", REFINED.err),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.err,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    cursor: "pointer",
    fontSize: "0.82rem",
    padding: "6px 12px",
    ":hover": { backgroundColor: REFINED.errBg },
  },
  fileBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    padding: "10px 14px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontSize: "0.88rem",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  // Kommunikation der Firma: EINE Spalte untereinander, halbe Kartenbreite —
  // eine Nummer über die volle Breite läse sich wie ein Freitext.
  kommSpalte: { display: "grid", rowGap: "16px", width: "min(100%, 500px)" },
  footerBar: {
    position: "sticky",
    bottom: 0,
    backgroundColor: REFINED.white,
    ...shorthands.borderTop("1px", "solid", REFINED.line),
    display: "flex",
    flexDirection: "column",
  },
  footer: {
    padding: "14px 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    columnGap: "12px",
  },
  cancelBtn: {
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "10px 20px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
  },
  saveBtn: {
    ...shorthands.borderWidth("0"),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.aub,
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    fontWeight: 600,
    padding: "10px 24px",
    cursor: "pointer",
    boxShadow: REFINED.btnShadow,
    transitionProperty: "background-color, box-shadow",
    transitionDuration: "140ms",
    ":hover": { backgroundColor: REFINED.aub700 },
    ":disabled": { opacity: 0.5, cursor: "default", boxShadow: "none" },
  },
  footerLeft: { display: "flex", alignItems: "center", columnGap: "12px" },
  /** „Daten befüllen" — sekundär wie Abbrechen, aber mit Symbol. */
  fillBtn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "7px",
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    backgroundColor: REFINED.white,
    color: REFINED.ink,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "10px 18px",
    cursor: "pointer",
    transitionProperty: "border-color, color",
    transitionDuration: "140ms",
    ":hover": { ...shorthands.borderColor(REFINED.aub), color: REFINED.aub },
    ":disabled": { opacity: 0.45, cursor: "default" },
  },
  footerRight: { display: "flex", alignItems: "center", columnGap: "12px" },
  /** Internet-Feld + „Daten befüllen" nebeneinander; der Knopf sitzt auf der
   *  Höhe des Eingabefelds (das Label darüber macht die Zeile ungleich hoch). */
  websiteRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    columnGap: "16px",
    alignItems: "end",
    "@media (max-width: 640px)": { gridTemplateColumns: "1fr" },
  },
  reqHint: { fontFamily: REFINED.fontUI, fontSize: "0.82rem", color: REFINED.err, fontWeight: 600 },
  // Sichtbarkeits-Hinweis: rahmenlos und ruhig (so will es der PO), aber in
  qLabel: { fontFamily: REFINED.fontUI, fontSize: "0.875rem", fontWeight: 600, color: REFINED.ink },
  field: { display: "grid", rowGap: "7px" },
});

/** Logo-Vorschlag im Bearbeiten-Modus der Akte (siehe `Props.logoAkte`). */
export interface LogoAkte {
  /** Hat die Firma schon ein Logo? Dann fragt der Dialog vor dem Ersetzen. */
  vorhanden: boolean;
  /** Das Logo ist gespeichert (`photo_url`, neuer Bearbeitungsstand). */
  onGespeichert: (photoUrl: string, version: number | null) => void;
}

type Props = {
  formFields: FormState;
  updateField: (name: string, value: string) => void;
  onSave: () => Promise<number | false>;
  onSaveSuccess: (contactId: number) => void;
  onClose: () => void;
  /**
   * Es kommt eine Rechtsform aus dem Anlage-Menü („Neu → Verein"). Dann gilt sie
   * als gewählt: die Autoerkennung aus dem Firmennamen darf sie nicht mehr
   * überschreiben — sonst wird aus dem Verein beim Tippen wieder eine GmbH.
   *
   * Bewusst „angefordert", nicht „aufgelöst": Der Aufrufer setzt das Flag schon
   * beim ersten Rendern, bevor sein Katalog-Fetch zurück ist. Sonst gäbe es ein
   * Zeitfenster, in dem die Autoerkennung die Vorbelegung noch kippt.
   */
  rechtsformVorbelegt?: boolean;
  /**
   * Umwandlungs-Modus: dasselbe Formular, vorausgefüllt aus einem BESTEHENDEN
   * Kontakt (Person → Firma). Siehe NewPrivateContactForm für das Gegenstück.
   */
  convert?: {
    from: "Firma" | "Person";
    relationsWithoutRole?: { name: string; previous: string }[];
    /** Felder des bisherigen Typs, die geleert werden. */
    fieldsToClear?: { label: string; value: string }[];
    /** Id des umzuwandelnden Kontakts — „Aus KMVS wählen" im Scan startet mit seiner Akte (T-41). */
    contactId?: number;
  };
  /** Einstieg über „Neuer Kontakt": Wechsel auf die Privatanlage. Fehlt er, ist der Typ fest. */
  onTypWechsel?: (typ: "person") => void;
  /** Bearbeiten-Modus der Kontaktakte — siehe NewPrivateContactForm. */
  bearbeiten?: { contactId: number; nurLesen?: boolean; gesperrt?: boolean; onPortfolio?: () => void; nachlauf?: AnlageNachlaufRef };
  /**
   * Beschriftung des Speichern-Knopfs — „Übernehmen"/„Weiter", wenn die
   * Anlage aus einer Schnellanlage geöffnet wurde (User 02.10.2026).
   */
  speichernText?: string;
  /**
   * Nur im Bearbeiten-Modus der Akte: Logo-Vorschlag aus „Daten befüllen".
   * Dort speichert das „Ja" sofort (wie ein hochgeladenes Foto) — die Akte
   * erfährt davon über `onGespeichert` und zeigt das Bild im Kopf.
   */
  logoAkte?: LogoAkte;
};

// „Sonstige" bedeutet bei einer FIRMA den Produktpartner
// (Versicherungsgesellschaft) plus die beiden Provisions-Status, die es für
// Firma und Person gleichermaßen gibt. Die frühere Sammelliste
// (Kooperationspartner, Versicherungsgesellschaft, Behörde/Gericht, Altkunde,
// Dienstleister, Blacklist) war reine Frontend-Konstante, ohne einen einzigen
// Kontakt im Bestand — „Altkunde" entsteht ohnehin nur über die
// Altkunden-Bereinigung, nicht über die Anlage.
// Die Unterkategorien einer FIRMA stehen nicht mehr als Konstante fest: Die
// vier CO-Arten (Lieferant, Sponsoring Partner, Mitgliedschaft/Verband,
// Direktionspartner) bekommt nur, wer `cointern:read` hat — sonst legte er
// einen Kontakt an, den er anschliessend selbst nicht mehr öffnen kann.
// `STATUS_PRODUKTPARTNER` bleibt für alle enthalten: an ihm hängt das
// verkürzte Partner-Layout.

const str = (v: unknown): string => (typeof v === "string" ? v : "");
const parseArr = (raw: unknown): any[] => {
  if (typeof raw !== "string" || !raw.trim()) return [];
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
};
/** Beschriftungen der Ausgangs-Beziehungen (nur im Umwandlungs-Modus gesetzt). */
const parseLabels = (raw: unknown): Record<string, string> => {
  if (typeof raw !== "string" || !raw.trim()) return {};
  try {
    const p = JSON.parse(raw);
    return p && typeof p === "object" && !Array.isArray(p) ? p : {};
  } catch {
    return {};
  }
};

const NewCompanyContactForm: React.FC<Props> = ({
  formFields,
  updateField,
  onSave,
  onSaveSuccess,
  onClose,
  rechtsformVorbelegt,
  convert,
  onTypWechsel,
  bearbeiten,
  logoAkte,
  speichernText,
}) => {
  const s = useStyles();
  const fs = useFormPrimitiveStyles();
  // Offene Vollanlagen der Schnellanlagen dieser Maske. In der Akte führt sie
  // die Akte selbst (ihr Schloss speichert ohne diese Maske).
  const eigeneVollanlagen = useOffeneVollanlagenRegister();
  const offeneVollanlagen = React.useContext(OffeneVollanlagenContext) ?? eigeneVollanlagen;
  const navigate = useNavigate();
  // „CO intern": Die vier CO-Arten stehen in der Statusauswahl nur
  // Berechtigten offen (siehe `sonstigeOptionenFirma`).
  const { hasPermission } = useAuth();

  const f = (k: string) => str(formFields[k]);
  const set = (k: string, v: string) => updateField(k, v);
  /**
   * PLZ oder Ort ändern — ein Ortsteil, der nicht mehr passt, fällt dabei weg
   * (utils/ortsteilAbgleich.ts, User 30.09.2026).
   */
  const setPlzOderOrt = (feld: "postalcode" | "city", v: string) => {
    const aenderung = feld === "postalcode" ? { plz: v } : { ort: v };
    if (ortsteilLeeren({ plz: f("postalcode"), ort: f("city"), ortsteil: f("cityArea") }, aenderung)) {
      set("cityArea", "");
    }
    set(feld, v);
  };
  const syncJson = (k: string, val: unknown) => updateField(k, JSON.stringify(val));
  const empty = (k: string) => f(k).trim() === "";
  // Pflichtfelder Firma: Firmenname, Rechtsform und E-Mail; eine Rufnummer
  // (Telefon ODER Mobil) kommt nur bei Kunde/Sonstige dazu. **Branche ist NICHT
  // mehr Pflicht** (SP4/D3); die Regel lebt in companyRequiredChecks (getestet).
  // Im Umwandlungs-Modus gilt **kein einziges** Pflichtfeld: der Kontakt
  // existiert bereits und wechselt nur seinen Typ — ein unvollständiger
  // Altbestand darf dabei nicht am Speichern gehindert werden. Ein leeres
  // Check-Array heißt: requiredOk ist true, es gibt nichts zu erfüllen.
  // Produktpartner (Versicherungsgesellschaft) statt Kunde/Interessent: die
  // Maske zeigt dann nur, was für eine Gesellschaft gilt — keine Branche,
  // Steuernummer, Bank, Herkunft oder Kundenverbindung.
  // Nur der Primärstatus bestimmt das Partnerlayout. Ein zusätzlicher
  // Partnerstatus an einem Kunden ändert dessen Formular nicht.
  const gewaehlteRollen = rollenAusFeld(f("kontaktStatusRollen"));
  const isProduktpartner =
    f("kontaktStatus") === STATUS_SONSTIGE &&
    !f("kontaktStatusAuch") && isPartnerStatus(gewaehlteRollen[0]);

  // Kontaktstatus — der FACHLICHE Wert, roh aus `kontaktStatus`. An ihm hängen
  // das Untertyp-Feld, die Herkunfts-Optionen und der NKG-Block, und er geht als
  // `statusName` an die API. „Interessent" ist der Default der Anlage, wie in
  // `buildCompanyContactCreatePayload`.
  //
  // Bewusst NICHT aus dem Segment abgeleitet: eine persönliche Interessenten-Firma
  // bleibt Interessent und behält damit ihren NKG-Block. Was das obere Segment
  // anzeigt, ist eine Projektion über zwei Feldern — siehe
  // data/kontaktstatusSegment.ts.
  const contactStatus = f("kontaktStatus") || "Interessent";
  const isInteressent = contactStatus === "Interessent";


  const requiredChecks = convert
    ? []
    : companyRequiredChecks({
        name: f("name"),
        gesellschaftsform: f("gesellschaftsform"),
        email: f("email"),
        phone: f("phone"),
        mobile: f("mobile"),
        isProduktpartner,
        isInteressent,
      });
  const requiredOk = requiredChecks.every(Boolean);
  const requiredFilled = requiredChecks.filter(Boolean).length;
  // FO oder BO ist Pflicht, um die Firma anzulegen (eins von beiden genügt).
  // Beim Umwandeln entfällt auch diese Sperre — der bestehende Kontakt bringt
  // seine Zuständigkeit bereits mit.
  // Beim Produktpartner entfällt sie ebenfalls: eine Versicherungsgesellschaft
  // hat keinen Kundenbetreuer — sie ist tenant-weit sichtbar.
  const foOrBoOk =
    convert ||
    !!bearbeiten ||
    isProduktpartner ||
    !!f("foUserId") ||
    (f("boType") === "TEAM" && !!f("boTeamId")) ||
    (f("boType") === "USER" && !!f("boUserId"));

  // Kein 01.01-Default mehr für das Geschäftsjahr: die Selects starten leer
  // (Platzhalter), weil ein vorbelegtes 01.01 von den Usern übersehen wurde.

  const [extraAddresses, setExtraAddresses] = React.useState<any[]>(() => parseArr(formFields.additionalAddresses));
  const [extraBanks, setExtraBanks] = React.useState<any[]>(() => parseArr(formFields.additionalBanks));
  const [weitereKommOpen, setWeitereKommOpen] = React.useState(false);
  const [extraCommunications, setExtraCommunications] = React.useState<WeitereKommunikationEintrag[]>(() => {
    try {
      const parsed = JSON.parse(String(formFields.weitereKommunikation ?? "[]"));
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const addExtraComm = (entry: WeitereKommunikationEintrag) => {
    const next = [...extraCommunications, entry];
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  const updateExtraComm = (id: string, patch: Partial<WeitereKommunikationEintrag>) => {
    const next = extraCommunications.map((c) => (c.id === id ? { ...c, ...patch } : c));
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  const removeExtraComm = (id: string) => {
    const next = extraCommunications.filter((c) => c.id !== id);
    setExtraCommunications(next);
    syncJson("weitereKommunikation", next);
  };

  // ── Erreichbarkeit ──
  const [erreichbarkeitModalOpen, setErreichbarkeitModalOpen] = React.useState(false);
  const [allgemeineErreichbarkeit, setAllgemeineErreichbarkeit] = React.useState<string>(
    () => str(formFields.erreichbarkeit) || "",
  );
  const [erreichbarkeiten, setErreichbarkeiten] = React.useState<Record<string, string>>(() => {
    try {
      const parsed = JSON.parse(String(formFields.erreichbarkeitenDetail ?? "{}"));
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  });

  const setErreichbarkeitField = (key: string, val: string) => {
    setErreichbarkeiten((prev) => {
      const next = { ...prev, [key]: val };
      syncJson("erreichbarkeitenDetail", next);
      return next;
    });
  };

  // Mobilnummer unter „Telefon" / Festnetz unter „Mobil": Hinweis + Knopf zum
  // Tauschen bzw. Verschieben (TelefonMobilHinweis.tsx). Über die normalen
  // Setter — die ids der Kommunikationszeilen hängen am Feld, nicht am Wert.
  const paarFirma = useTelefonMobilPaar({
    telefon: f("phone"),
    mobil: f("mobile"),
    setTelefon: (v) => set("phone", v),
    setMobil: (v) => set("mobile", v),
    erreichbarkeitTauschen: (aktion) =>
      setErreichbarkeiten((prev) => {
        if (!prev.phone && !prev.mobile) return prev;
        const next = erreichbarkeitUmhaengen(prev, "phone", "mobile", aktion);
        syncJson("erreichbarkeitenDetail", next);
        return next;
      }),
  });

  // Doppelte Nummer/E-Mail in derselben Firma bzw. Nummer eines anderen
  // Kontakts (User 01.10.2026): nur Hinweise unter dem Feld. Dieselbe Liste
  // bekommt das Popup „Weiterer Kommunikationsweg".
  const kontaktWege: KontaktWeg[] = [
    { key: "phone", typ: "phone", wert: f("phone"), feldname: isProduktpartner ? "Telefon (Zentrale)" : "Telefon" },
    { key: "mobile", typ: "mobile", wert: f("mobile"), feldname: "Mobil" },
    { key: "email", typ: "email", wert: f("email"), feldname: isProduktpartner ? "E-Mail (Zentrale)" : "E-Mail" },
    { key: "fax", typ: "fax", wert: f("fax"), feldname: "Fax" },
    ...weitereKommWege(extraCommunications, { mitZuordnung: false, faxHauptfeld: true }),
  ];
  const doppelt = doppelteWege(kontaktWege);
  const eigeneKontaktId = bearbeiten?.contactId ?? convert?.contactId ?? null;
  const andereKontakte = useNummernAndererKontakte(kontaktWege, {
    eigeneId: eigeneKontaktId,
    nurGeaenderte: eigeneKontaktId != null,
  });
  /** Hinweise unter einem Feld: vertauscht (Telefon/Mobil), doppelt, anderer Kontakt. */
  const wegHinweise = (key: string, typ: WegTyp, telMobil?: "telefon" | "mobil") => (
    <KommHinweise
      eigen={[
        telMobil ? telefonMobilKommHinweis(telMobil, paarFirma.hinweis, paarFirma.ausfuehren) : null,
        doppeltKommHinweis(typ, doppelt.get(key)),
        doppelt.has(key) ? null : andererKontaktKommHinweis(andereKontakte.get(key)),
      ]}
    />
  );

  const handleAllgemeinChange = (val: string) => {
    setAllgemeineErreichbarkeit(val);
    set("erreichbarkeit", val);
  };

  const renderCommLabel = (
    baseTitle: string,
    bezeichnung?: string
  ) => {
    const parts: string[] = [];
    if (bezeichnung && bezeichnung.trim()) {
      const bTrim = bezeichnung.trim();
      if (!["telefon", "mobil", "e-mail", "email", "fax"].includes(bTrim.toLowerCase())) {
        parts.push(bTrim);
      }
    }
    const suffix = parts.join(" - ");

    return (
      <span>
        <span>{baseTitle}</span>
        {suffix ? (
          <span
            style={{
              color: "#736871",
              fontSize: "0.75rem",
              fontWeight: 400,
              marginLeft: "6px",
            }}
          >
            {suffix}
          </span>
        ) : null}
      </span>
    );
  };

  const getPlainCommLabel = (
    baseTitle: string,
    bezeichnung?: string
  ) => {
    const parts: string[] = [];
    if (bezeichnung && bezeichnung.trim()) {
      const bTrim = bezeichnung.trim();
      if (!["telefon", "mobil", "e-mail", "email", "fax"].includes(bTrim.toLowerCase())) {
        parts.push(bTrim);
      }
    }
    const suffix = parts.join(" - ");
    return suffix ? `${baseTitle} (${suffix})` : baseTitle;
  };

  const [vereine, setVereine] = React.useState<VereinEintrag[]>(() => {
    try {
      const raw = String(formFields.vereine ?? "[]");
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    } catch {
      // ignore
    }
    if (typeof formFields.memberships === "string" && formFields.memberships.trim()) {
      return formFields.memberships.split(";").map((part, idx) => ({
        id: `v-${idx}`,
        name: part.trim(),
        istMitglied: true,
      }));
    }
    return [];
  });

  const onVereineChange = (next: VereinEintrag[]) => {
    setVereine(next);
    syncJson("vereine", next);
    const summary = next
      .map((v) => (v.vereinName || v.ref?.lastName || "").trim())
      .filter(Boolean)
      .join("; ");
    set("memberships", summary);
  };

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // Im Handelsregister ausgewählte Personen. Verbunden werden sie erst, wenn
  // die Firma eine Id hat — siehe `runSubmit`.
  const [registerPersonen, setRegisterPersonen] = React.useState<UebernahmeEintrag[]>([]);
  // Nach jedem Speichern der Akte: die Registerknöpfe lesen ihren Stand
  // (gespeicherte Nummer, abgelegte Abdrucke) neu.
  const [registerStandVersion, setRegisterStandVersion] = React.useState(0);
  // Gesellschaftsvertrag/Satzung: vorgemerkt, abgelegt erst nach dem Anlegen.
  // Beim Verein ist das die Satzung — EIN Zustand für beide (User 30.09.2026).
  const [vertragDatei, setVertragDatei] = React.useState<File | null>(null);
  /** Schon in der Akte abgelegt (Verein im Bearbeiten-Modus: sofort beim Auswählen). */
  const [vertragAbgelegt, setVertragAbgelegt] = React.useState(false);
  const [vertragAusRegister, setVertragAusRegister] = React.useState(false);
  /** Verbindungen, die beim Speichern ersatzlos entfallen — erst nach Nachfrage. */
  const [removalPrompt, setRemovalPrompt] = React.useState<string[] | null>(null);

  const pendingPhotoRef = React.useRef<File | null>(null);
  /** Dateien zu Einladungen (Karte Zuwendungen) — nach dem Anlegen abgelegt. */
  const pendingEinladungFilesRef = React.useRef<File[]>([]);
  const [photoName, setPhotoName] = React.useState<string>(f("photo_url"));

  // NKG-Vorgang (Neukundengewinnung) — Inline-ja/nein statt Browser-Popup. Default „Nein".
  const [nkgWanted, setNkgWanted] = React.useState(false);
  // Karte „Vollmachten": Auswahl vor dem Speichern, erzeugt wird danach.
  const [vollmachten, setVollmachten] = React.useState<VollmachtAuswahl[]>(standardVollmachtAuswahl);

  // Finanzamt-Prefill aus Steuernummer-BUFA nur, solange der Nutzer das Feld
  // nicht selbst bearbeitet hat.
  const finanzamtTouchedRef = React.useRef(false);

  /**
   * Übernimmt eine gewählte Gesellschaft in die Maske. Überschreibt bewusst nur
   * LEERE Felder (außer Name und Id) — wer vor der Auswahl schon etwas getippt
   * hat, soll es nicht verlieren. Abwahl räumt nur die Verknüpfung ab, nicht die
   * bereits übernommenen Daten.
   */
  const applyGesellschaft = (g: GesellschaftDetail | null) => {
    if (!g) {
      set("gesellschaftId", "");
      return;
    }
    set("gesellschaftId", String(g.id));
    set("name", g.name);
    const fill = (key: string, value?: string | null) => {
      if (!empty(key)) return;
      const v = String(value ?? "").trim();
      if (v) set(key, v);
    };
    fill("street", g.strasse);
    fill("postalcode", g.plz);
    fill("city", g.ort);
    fill("phone", g.telefon);
    fill("fax", g.fax);
    fill("email", g.email);
    fill("website", g.website);
  };

  // ── „Daten befüllen": öffentliche Angaben aus dem Impressum ────────────────
  const [befuellenOffen, setBefuellenOffen] = React.useState(false);
  const [scanOffen, setScanOffen] = React.useState(false);

  // Gesucht wird mit dem Namen, geladen mit der Website oder der Domain der
  // E-Mail. Eins von beidem reicht — mit der Website allein kommen Name und
  // Rechtsform aus dem Impressum (T-323).
  const befuellenGesperrt = !befuellenMoeglich({ name: f("name"), website: f("website"), email: f("email") });

  /**
   * Übernimmt die im Dialog gewählten Angaben. Die Schlüssel entsprechen bereits
   * den Feldnamen der Maske, deshalb genügt `set` — und wie bei
   * `applyGesellschaft` werden nur LEERE Felder gefüllt: was der Anwender selbst
   * getippt hat, bleibt stehen. Der Dialog sperrt gefüllte Felder ohnehin schon,
   * die Prüfung hier ist die zweite Sicherung.
   */
  const applyImpressum = (
    felder: Partial<Record<string, string | null>>,
    weitere?: { phone: string[]; email: string[] },
  ) => {
    // Zusätzliche Rufnummern und Adressen: Die Maske hat je Art EIN Feld, die
    // Firma aber beliebig viele Kommunikationszeilen. Sie werden hier
    // gesammelt und beim Speichern als eigene Zeilen angelegt (siehe
    // `buildCompanyContactCreatePayload`) — sonst müsste der Anwender sich für
    // eine Nummer entscheiden und den Rest hinterher in der Akte nachtragen.
    const zusatz = [
      ...(weitere?.phone ?? []).map((wert) => ({ id: neueWegId(), type: "phone", value: wert, quelle: "impressum" as const })),
      ...(weitere?.email ?? []).map((wert) => ({ id: neueWegId(), type: "email", value: wert, quelle: "impressum" as const })),
    ]
      // Die Hauptangabe steht im festen Feld — seit die weiteren Wege sichtbar
      // in der Karte stehen, stünde sie sonst ein zweites Mal als „Telefon 2".
      .filter((z) => {
        const w = z.value.trim().toLowerCase();
        const haupt = [felder.phone, felder.email, f("phone"), f("email")]
          .map((v) => String(v ?? "").trim().toLowerCase())
          .filter(Boolean);
        return w && !haupt.includes(w);
      });
    if (zusatz.length) {
      let vorhanden: Array<{ type: string; value: string }> = [];
      try {
        const roh = JSON.parse(String(formFields.weitereKommunikation ?? "[]"));
        if (Array.isArray(roh)) vorhanden = roh;
      } catch {
        /* unlesbarer Altstand wird ersetzt, nicht beklagt */
      }
      const gesehen = new Set(
        vorhanden.map((z) => `${z.type}|${z.value.trim().toLowerCase()}`),
      );
      const neu = zusatz.filter(
        (z) => !gesehen.has(`${z.type}|${z.value.trim().toLowerCase()}`),
      );
      if (neu.length) {
        // Über den Zustand der Karte, sonst stünden die Treffer nicht in der
        // Maske und das nächste Bearbeiten eines Wegs würfe sie wieder raus.
        const alle = [...vorhanden, ...neu] as unknown as WeitereKommunikationEintrag[];
        setExtraCommunications(alle);
        set("weitereKommunikation", JSON.stringify(alle));
      }
    }

    // PLZ/Ort, die gleich gesetzt werden — ein alter Ortsteil passt dann nicht
    // mehr (ortsteilAbgleich.ts). Das Impressum liefert keinen Ortsteil.
    const neuePlz = String(felder.postalcode ?? "").trim();
    const neuerOrt = String(felder.city ?? "").trim();
    if (
      ortsteilLeeren(
        { plz: f("postalcode"), ort: f("city"), ortsteil: f("cityArea") },
        {
          ...(neuePlz && empty("postalcode") ? { plz: neuePlz } : {}),
          ...(neuerOrt && empty("city") ? { ort: neuerOrt } : {}),
        },
      )
    ) {
      set("cityArea", "");
    }

    for (const [key, wert] of Object.entries(felder)) {
      const v = String(wert ?? "").trim();
      if (!v) continue;
      // Der Firmenname ist das einzige Feld, das Getipptes ERSETZT: gesucht wird
      // mit der Kurzform, im Impressum steht der vollständige Rechtsträger. Der
      // Dialog bietet ihn nur mit gesetztem Haken an. Mit dem neuen Namen wird
      // auch die Rechtsform neu erkannt (… GmbH & Co. KG).
      if (key === "name") {
        set("name", v);
        applyLegalFormFromName(v);
        continue;
      }
      if (!empty(key)) continue;
      set(key, v);
    }
  };

  /**
   * Übernimmt die Werte einer eingelesenen Visitenkarte.
   *
   * Wie `applyImpressum`: nur leere Felder werden gefüllt — der Dialog bietet
   * belegte Felder gar nicht erst zur Übernahme an, und was hier trotzdem
   * ankommt, soll Getipptes nicht überschreiben. Ausnahme ist auch hier der
   * Firmenname; die Rechtsform kommt in dem Fall aber schon aus dem Mapper
   * (mit der Katalog-Id, die zwischen DEV und PROD abweicht), deshalb wird
   * `applyLegalFormFromName` nur als Rückfallebene angestoßen.
   */
  const applyDateiScan = (ergebnis: DateiScanUebernahme) => {
    const name = (ergebnis.werte.name ?? "").trim();
    if (name) {
      set("name", name);
      if (!ergebnis.werte.gesellschaftsform) applyLegalFormFromName(name);
    }

    for (const [key, wert] of Object.entries(ergebnis.werte)) {
      if (key === "name") continue;
      const v = String(wert ?? "").trim();
      if (!v) continue;
      if (!empty(key)) continue;
      set(key, v);
    }

    // Die Person der Karte kommt zu den „Menschen fuers Unternehmen". Sie traegt
    // `manual: true` und wird erst beim Speichern der Firma angelegt — so, wie
    // es die Inline-Verknuepfungen ohnehin handhaben (nichts wird waehrend des
    // Ausfuellens geschrieben).
    if (ergebnis.mensch) {
      let bisher: any[] = [];
      try {
        const roh = JSON.parse(String(formFields.rel_menschen ?? "[]"));
        if (Array.isArray(roh)) bisher = roh;
      } catch {
        /* unlesbarer Altstand wird ersetzt, nicht beklagt */
      }
      // Namentlich schon dabei → ergänzen statt doppelt eintragen (V8).
      const next = menschEintragen(bisher, ergebnis.mensch);
      if (next !== bisher) set("rel_menschen", JSON.stringify(next));
    }
  };

  // Branchen der Firma: erste = Hauptbranche (Kontaktspalte), darunter weitere.
  // Der State wird aus den formFields gespeist, damit ein Fenster-Wechsel die
  // Eingaben nicht verliert. Der Freitext MUSS in beiden Richtungen des
  // JSON-Roundtrips mitlaufen — sonst ist er nach einem Fenster-Wechsel weg,
  // ohne dass irgendetwas fehlschlägt.
  const [brancheEintraege, setBrancheEintraege] = React.useState<BrancheGruppenEintrag[]>(() => {
    const haupt: BrancheGruppenEintrag = {
      branchengruppeId: formFields.branchengruppeId ? Number(formFields.branchengruppeId) : null,
      freitext: String(formFields.brancheFreitext ?? ""),
    };
    try {
      const weitere = JSON.parse(String(formFields.weitereBranchengruppen ?? "[]"));
      if (Array.isArray(weitere) && weitere.length) {
        return [
          haupt,
          ...weitere.map((w: any) => ({
            branchengruppeId: w?.branchengruppeId != null ? Number(w.branchengruppeId) : null,
            freitext: String(w?.freitext ?? ""),
          })),
        ];
      }
    } catch {
      /* kaputter Zwischenstand → nur die Hauptbranche */
    }
    return [haupt];
  });

  // Geschäftsjahr: Vorbelegung auf den 01.01. (PO-Notiz 02.08.). Das
  // Kalenderjahr ist der Normalfall; ohne Vorbelegung blieb das Feld in aller
  // Regel leer, und in der Detailansicht stand später nichts. Wer ein
  // abweichendes Geschäftsjahr hat, stellt die beiden Felder um. Im
  // Umwandlungs-Modus wird nichts vorbelegt — dort gilt der Bestand.
  React.useEffect(() => {
    // Auch im Bearbeiten-Modus nicht: Die Vorbelegung stünde im Ladestand der
    // Akte, würde angezeigt, aber nie gespeichert (Befund 28.09.2026).
    if (convert || bearbeiten) return;
    if (!f("geschaeftsjahrTag") && !f("geschaeftsjahrMonat")) {
      set("geschaeftsjahrTag", "01");
      set("geschaeftsjahrMonat", "01");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rechtsform-Autoerkennung aus dem Firmennamen (z. B. „… GmbH" → GmbH).
  // Optionen vom Backend; nur vorbelegen, solange die Rechtsform nicht manuell
  // gewählt/geändert wurde.
  const [legalFormOptions, setLegalFormOptions] = React.useState<LegalFormOption[]>([]);
  // Eine vorbelegte Rechtsform zählt wie eine gewählte. Die Markierung wird
  // SOFORT beim Mounten gesetzt — nicht erst, wenn die Rechtsform aufgelöst ist:
  // Diese Maske lädt den Rechtsform-Katalog über einen ZWEITEN eigenen Request.
  // Käme der zuerst zurück, könnte die Autoerkennung beim Tippen des
  // Firmennamens die Vorbelegung noch überschreiben — aus dem Verein würde
  // wieder eine GmbH. Dass eine Rechtsform kommt, weiß der Aufrufer bereits vor
  // seinem Fetch, deshalb genügt hier der Initialwert des Refs.
  const legalFormTouchedRef = React.useRef(!!rechtsformVorbelegt);
  React.useEffect(() => {
    let active = true;
    fetchLegalFormOptions()
      .then((opts) => {
        if (active) setLegalFormOptions(opts);
      })
      .catch(() => {
        /* Rechtsform-Optionen optional — Auto-Erkennung entfällt still */
      });
    return () => {
      active = false;
    };
  }, []);

  // Auswahl für den Rechtsvorgänger. Ein inzwischen deaktivierter
  // Katalogeintrag fehlt in der Liste — er bleibt dann unter seinem
  // gespeicherten Namen wählbar, statt still zu verschwinden.
  const rechtsvorgaengerOptionen = React.useMemo(() => {
    const liste = legalFormOptions.map((o) => ({ value: String(o.id), label: o.legalForm }));
    const id = str(formFields.oldLegalFormId);
    if (id && !liste.some((o) => o.value === id)) {
      liste.push({ value: id, label: str(formFields.oldLegalFormName) || "(nicht mehr im Katalog)" });
    }
    return liste;
  }, [legalFormOptions, formFields.oldLegalFormId, formFields.oldLegalFormName]);

  // Beschriftungen nach der GEWÄHLTEN Rechtsform: „Name des Vereins" statt
  // „Firmenname" (PO 19.08.2026). Sie hängen an der Auswahl, nicht am Einstieg
  // — wer hier auf „Eingetragener Verein (e.V.)" umstellt, sieht sie sofort
  // wechseln. Der Produktpartner behält seine eigenen Texte („Name der
  // Gesellschaft"): eine Gesellschaft ist auch dann keine Körperschaft im Sinne
  // dieser Maske, wenn sie als VVaG geführt wird. Die Rechtsform selbst geht
  // mit, weil die eGbR ihr Register anders nennt als jede andere Firma (T-329).
  const koerperschaft = React.useMemo(
    () =>
      isProduktpartner
        ? KOERPERSCHAFT_FIRMA
        : koerperschaftTexte(
            unternehmensBegriffAus(
              legalFormOptions,
              Number(f("legalFormId")) || null,
              f("gesellschaftsform"),
            ),
            rechtsformOptionAus(
              legalFormOptions,
              Number(f("legalFormId")) || null,
              f("gesellschaftsform"),
            ) ?? { legalForm: f("gesellschaftsform") },
          ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProduktpartner, legalFormOptions, formFields.legalFormId, formFields.gesellschaftsform],
  );

  // Gemeinnützigkeit nur, wo die Rechtsform sie führt (Verein, Stiftung,
  // Genossenschaft). Wechselt die Rechtsform weg, gehen die Angaben nicht
  // unsichtbar mit — sonst speicherte eine GmbH ein „gemeinnützig" aus dem
  // kurzen Umweg über den Verein.
  const zeigeGemeinnuetzig = !isProduktpartner && fuehrtGemeinnuetzigkeit(koerperschaft);
  // Verein: keine Unternehmensfunktion, „Satzungszweck" statt
  // „Tätigkeitsbeschreibung", Branche darunter (User 28.09.2026).
  const istVerein = !isProduktpartner && koerperschaft.art === "verein";
  React.useEffect(() => {
    if (zeigeGemeinnuetzig) return;
    for (const k of ["istGemeinnuetzig", "freistellungsbescheidBis", "zweckNrAO"]) {
      if (f(k)) set(k, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zeigeGemeinnuetzig]);

  /**
   * Hinweis unter der Rechtsform, wenn der Name eine Rechtsform nahelegt, die
   * die Maske NICHT setzen darf (mehrdeutig oder ohne Katalogeintrag).
   */
  /**
   * Vorbelegung des Kontoinhabers: Firmenname samt Zusatz, ohne
   * Rechtsform-Kürzel (das tragen die Mitarbeiter selbst ein). Wird beim
   * Speichern WIRKLICH geschrieben (buildCompanyContactCreatePayload). Solange
   * das Feld leer ist, folgt der Vorschlag dem Namen; ein eigener Eintrag
   * bleibt stehen.
   */
  const kontoinhaberName = kontoinhaberVorschlag({ lastname: f("name"), suffix: f("suffix") }, true);
  const [legalFormHinweis, setLegalFormHinweis] = React.useState<string | null>(null);
  const applyLegalFormFromName = React.useCallback(
    (nextName: string) => {
      if (legalFormTouchedRef.current) return;
      if (!legalFormOptions.length) return;
      const { befund, option } = rechtsformAusName(nextName, legalFormOptions);
      // Genau 30 Zeichen: der Alt-Import hat dort gekappt („… GmbH & Co. K").
      // Was wie eine vollständige Rechtsform aussieht, kann der Rest einer
      // anderen sein — hier wird weder vorgeschlagen noch gemutmaßt.
      if (befund.trunkiert) {
        setLegalFormHinweis(null);
        return;
      }
      if (befund.konfidenz === "SICHER" && option) {
        set("gesellschaftsform", option.legalForm);
        set("legalFormId", String(option.id));
        setLegalFormHinweis(null);
        return;
      }
      // Unsicher (SE, VVaG/a.G., „… & Co. KG" mit fremdem Komplementär) oder im
      // Katalog nicht vorhanden: anbieten statt setzen.
      setLegalFormHinweis(
        befund.key && befund.konfidenz !== "KEINE"
          ? `Der Name deutet auf ${RECHTSFORM_BEZEICHNUNG[befund.key]} hin — bitte selbst wählen.`
          : null,
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [legalFormOptions],
  );

  // Format-Gate (block + warn): jedes BEFÜLLTE Feld muss gültig sein (leer = ok),
  // inkl. der Wiederhol-Blöcke. Speichern ist nur bei gültigem Format möglich.
  // Weitere Kommunikationswege (zweite Zentrale, Vertrieb …) — JSON im Formular,
  // dasselbe Feld, in das auch der Impressum-Abgleich schreibt.
  const weitereWege = wegeLesen(formFields.weitereKommunikation);
  // Wahl der Newsletter-Adressen: alle E-Mail-Adressen, die gerade in der Maske stehen.
  const newsletterEmailOptionen = [f("email"), ...weitereWege.filter((w) => w.type === "email").map((w) => w.value)]
    .map((e) => (e ?? "").trim())
    .filter((e, i, alle) => e && alle.indexOf(e) === i)
    .map((e) => ({ value: e, label: e }));

  const formatValid = [
    ...weitereWege.map(wegFormatOk),
    validatePhone(f("phone")).valid,
    validatePhone(f("fax")).valid,
    validateEmail(f("email")).valid,
    validateIban(f("iban")).valid,
    validateBic(f("bic")).valid,
    validateUstIdNr(f("umsatzsteuernummer")).valid,
    validateSteuernummer(f("steuernummer")).valid,
    // Gesperrt wird nur, wo die Regel bewiesen ist — bei ausdrücklich
    // deutschen Adressen. Eine belgische PLZ bekommt einen Hinweis, aber
    // der Speichern-Knopf bleibt offen.
    !plzSperrtSpeichern(f("postalcode"), f("country")),
    ...extraAddresses.map((a) => !plzSperrtSpeichern(str(a.postalCode), str(a.country))),
    ...extraBanks.flatMap((b) => [validateIban(str(b.iban)).valid, validateBic(str(b.bic)).valid]),
  ].every(Boolean);

  const { contactOrigins, contactOriginOther, toggleContactOrigin, setContactOriginOtherValue } =
    useContactOrigins({
      formFields,
      updateField,
      // Die Auswahl steht im Formular (herkunftEmpfehlungsgeber/-Tippgeber);
      // der Hook leert sie dort selbst.
      onClearReferrer: () => {},
      onClearTippgeber: () => {},
      onClearKundenverbindung: () => {},
    // Herkunft gehört zur Anlage; im Umwandlungs-Modus würde der
    // Default-Effekt das Formular ungefragt als geändert markieren.
    enabled: !convert && !bearbeiten,
    });

  const dup = useDuplicateCheck({
    isCompany: true,
    firstName: "",
    lastName: f("name"),
    birthDate: "",
    email: f("email"),
    // Beim Umwandeln aus: die Maske ist mit dem Namen des bestehenden Kontakts
    // vorausgefüllt, die Suche fände also ihn selbst und meldete ihn als
    // Doppelgänger.
    enabled: !convert && !bearbeiten,
  });

  // ── Logo ────────────────────────────────────────────────────────────────
  const onPhotoPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    pendingPhotoRef.current = file;
    setPhotoName(file.name);
    set("photo_url", file.name);
  };
  /**
   * Logo-Vorschlag aus „Daten befüllen" („Ist das das Logo der Firma?").
   *
   * Anlage: Das Bild wird wie ein ausgewähltes Foto gemerkt und erst beim
   * Speichern hochgeladen (`uploadPendingPhoto`) — vorher gibt es die Firma
   * nicht. Akte (`bearbeiten`): sofort speichern, wie der Foto-Upload im Kopf;
   * ein vorhandenes Logo nur nach der Rückfrage im Dialog.
   */
  const logoFrage: LogoFrage | undefined = isProduktpartner
    ? undefined
    : bearbeiten
      ? logoAkte && !bearbeiten.nurLesen
        ? {
            vorhanden: logoAkte.vorhanden,
            onJa: async (bild, ersetzen) => {
              const r = await logoUebernehmen(bearbeiten.contactId, bild, ersetzen);
              logoAkte.onGespeichert(r.photo_url, r.version);
            },
          }
        : undefined
      : {
          vorhanden: !!pendingPhotoRef.current || !!f("photo_url"),
          onJa: (bild) => {
            const datei = datenUrlAlsDatei(bild, "logo-von-der-website");
            pendingPhotoRef.current = datei;
            setPhotoName(datei.name);
            set("photo_url", datei.name);
          },
        };

  const uploadPendingPhoto = async (contactId: number): Promise<void> => {
    const file = pendingPhotoRef.current;
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await fetch(apiUrl("upload/contact-photo"), { method: "POST", body: fd });
      if (!up.ok) return;
      const { url } = await up.json();
      await fetch(apiUrl(`contact/${contactId}`), {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ photo_url: url }),
      });
      pendingPhotoRef.current = null;
    } catch {
      /* Logo-Fehler darf Anlage nicht abbrechen */
    }
  };

  // ── Gesammelte Verknüpfungen persistieren (ERST nach Anlage der Firma) ────
  // Inline gesammelt (rel_*-Keys); frei eingetippte Sub-Kontakte werden hier
  // angelegt, dann die Beziehungen + KV-Zuweisung. Nichts davon vorher.
  /**
   * Beziehungs-Zeilen, die der `attach-menschen`-Endpoint verwaltet: Er ersetzt
   * die Rollen eines Person↔Firma-Paars transaktional. Sie gehören deshalb nicht
   * in den Lösch-Schritt des Abgleichs — der löschte sonst, was gerade neu
   * geschrieben wurde. Nur Einträge, die der Anwender GANZ entfernt hat, fehlen
   * hier und bleiben damit zum Löschen übrig.
   */
  const menschenRelationIds = (): Set<number> => {
    const out = new Set<number>();
    for (const m of parseArr(formFields.rel_menschen) as Array<{ relationIds?: unknown }>) {
      if (!Array.isArray(m?.relationIds)) continue;
      for (const id of m.relationIds) if (typeof id === "number") out.add(id);
    }
    return out;
  };

  /** Ausgangsbestand, für den der Abgleich zuständig ist. */
  const syncPreviousIds = (): number[] => {
    const owned = menschenRelationIds();
    return (parseArr(formFields.rel_previousIds) as unknown as number[]).filter(
      (id) => typeof id === "number" && !owned.has(id),
    );
  };

  const persistRelations = async (contactId: number, sync = false): Promise<void> => {
    // FO (auch mehrere), BO und persönlicher Inhouse-Kontakt — dieselbe
    // Verantwortlichkeit wie der Hauptkontakt (User 28.09.2026).
    const ownership = ownershipAusFormular(formFields);
    // Menschen fürs Unternehmen (GF/Gesellschafter + Verantwortlichkeiten) → über den
    // transaktionalen Endpoint: legt Sub-Kontakte an, erbt FO/BO, setzt Status, Relationen.
    const menschen = parseArr(formFields.rel_menschen);
    let menschenOk = true;
    if (menschen.length) {
      const items = buildMenschenItems(menschen, contactId);
      if (items.length) {
        try {
          // Beim Umwandeln `replace`: der Endpoint löscht pro Person↔Firma-Paar
          // richtungsunabhängig alle verwalteten Rollen und schreibt den Satz
          // neu. Ohne das Flag entstünde neben jeder vorausgefüllten Rolle eine
          // zweite Zeile. Andere Beziehungstypen des Paars bleiben unberührt.
          await attachMenschen(items, sync);
        } catch {
          menschenOk = false;
          await hinweisen({
            titel: "Firma angelegt – mit einer Lücke",
            text: "Die Firma ist gespeichert, aber die Verantwortung fürs Unternehmen konnte nicht übernommen werden. Bitte in der Akte nachtragen.",
            ton: "danger",
          });
        }
      }
    }
    // Verbindungen zu Firmen + Kundenverbindung (client-seitig).
    // Verbindungen zu Firmen + die Gegenüber der Statusrollen. Beides geht
    // durch denselben Abgleich: `persistCollectedRelations` entscheidet je Art,
    // welcher Beziehungstyp entsteht (`data/statusBezugMapping.ts`).
    //
    // Ein Bezug ohne Gegenkontakt fällt hier heraus, statt später still zu
    // scheitern — die Maske weist darunter darauf hin, dass die Rolle dann
    // ohne Bezug bleibt.
    const firmaConn = {
      firmaConn: parseArr(formFields.rel_firmaConn),
      // Vereins-Mitgliedschaften (Karte „Mitgliedschaften"): dieselbe Zeile
      // wie bei der Person — Verein → Firma, „Mensch fürs Unternehmen", ohne
      // Funktion „Mitglied". Ein neu eingetippter Verein entsteht dabei mit
      // der Verantwortung der Firma. Bis 02.10.2026 ging die Karte verloren.
      personFirma: vereineAlsVerbindungen(vereine),
      statusBezug: bezuegeAusFeld(formFields.kontaktStatusBezuege)
        .filter((b) => b.ref)
        // `id` ist eine reine UI-Kennung; die Rolle ist je Kontakt eindeutig
        // und taugt damit als stabiler Schlüssel.
        .map((b) => ({ id: `bezug-${b.rolle}`, ref: b.ref, rolle: b.rolle })),
    };
    if (sync) {
      // Vorausgefüllte Einträge tragen ihre Zeilen-Ids → PATCH statt POST,
      // entfernte werden gelöscht. Sonst stünde jede Verbindung doppelt da.
      //
      // Der Lösch-Schritt darf NUR Zeilen sehen, für die dieser Abgleich
      // zuständig ist. Die Rollen der Menschen fürs Unternehmen verwaltet
      // `attach-menschen` (ersetzt sie transaktional) — stünden sie hier mit
      // drin, löschte der Abgleich, was der Endpoint gerade geschrieben hat.
      // Und wenn er fehlschlug, löschte er die noch bestehenden Rollen. Nur
      // Einträge, die der Anwender ganz entfernt hat, bleiben zum Löschen übrig.
      const previousIds = syncPreviousIds();
      const report = await syncCollectedRelations(
        contactId,
        firmaConn,
        // Nach einem gescheiterten attach-menschen wird nichts gelöscht: sonst
        // wären die alten Rollen weg und die neuen nie geschrieben worden.
        menschenOk ? previousIds : [],
        ownership,
      );
      if (report.failures.length) {
        await hinweisen({
          titel: "Nicht alle Verbindungen übernommen",
          text: "Die Firma ist gespeichert, aber diese Verbindungen konnten nicht übernommen werden:\n\n" + report.failures.join("\n"),
          ton: "danger",
        });
      }
    } else {
      await persistCollectedRelations(contactId, firmaConn, ownership);
    }
    const kv = f("rel_kvGroupId");
    if (kv) await assignKundenverbindung(contactId, Number(kv));
  };

  /**
   * Die im Register ausgewählten Personen anlegen und verbinden — erst jetzt
   * möglich, weil es die Firma vorher nicht gab. Ein Fehler darf die Anlage
   * nicht kippen: Die Firma steht, die Personen lassen sich in den Details
   * jederzeit nachholen.
   */
  const uebernehmeRegisterPersonen = async (contactId: number): Promise<void> => {
    if (!registerPersonen.length) return;
    try {
      const r = await registerBeteiligteUebernehmen(contactId, registerPersonen);
      if (r.fehler.length) {
        await hinweisen({
          titel: "Nicht alle Personen übernommen",
          text:
            "Die Firma ist gespeichert, aber diese Personen aus dem Handelsregister konnten nicht übernommen werden:\n\n" +
            r.fehler.map((f) => `${f.name}: ${f.grund}`).join("\n"),
          ton: "danger",
        });
      }
    } catch (err) {
      await hinweisen({
        titel: "Personen nicht übernommen",
        text:
          "Die Firma ist gespeichert, die Personen aus dem Handelsregister konnten aber nicht übernommen werden.\n\n" +
          (err instanceof Error ? err.message : String(err)),
        ton: "danger",
      });
    }
  };

  /**
   * Neuanlage: Wurden Personen aus dem Register vorgemerkt, gehört der Abdruck
   * als Beleg in die neue Akte (User 01.10.2026). Die Vorschau lief ohne Akte
   * und hat ihn nicht mitgebracht — also EIN Abruf jetzt. Ein Fehler kippt die
   * Anlage nicht; der Abdruck lässt sich in der Akte jederzeit holen.
   */
  const legeRegisterAbdruckAb = async (contactId: number): Promise<void> => {
    if (!registerPersonen.length) return;
    try {
      await registerAbdruckAbrufen(contactId);
    } catch (err) {
      await hinweisen({
        titel: "Abdruck nicht abgelegt",
        text:
          "Die Firma ist gespeichert, der Handelsregister-Abdruck konnte aber nicht abgelegt werden. " +
          "Er lässt sich in der Akte über „Aktuellen Abdruck holen“ nachholen.\n\n" +
          (err instanceof Error ? err.message : String(err)),
        ton: "danger",
      });
    }
  };

  /**
   * Weitere Branchen nachreichen — sie brauchen die Kontakt-id und laufen
   * deshalb nach dem Anlegen über /contact/:id/branchengruppen. Die
   * HAUPTbranche ist schon im Kontakt-Payload enthalten; sie geht hier
   * TROTZDEM mit, weil die Zuordnungstabelle die vollständige Liste führt
   * (erste Zeile = Hauptbranche). Ein Fehler hier darf die fertige Anlage
   * nicht kippen; er wird gemeldet, der Kontakt bleibt bestehen.
   */
  const speichereWeitereBranchen = async (contactId: number) => {
    const alle = brancheEintraege
      .filter((e) => e.branchengruppeId != null)
      .map((e) => ({
        branchengruppeId: e.branchengruppeId as number,
        freitext: e.freitext.trim() || null,
      }));
    if (alle.length === 0) return;
    try {
      await branchengruppenSpeichern(contactId, alle);
    } catch (e) {
      console.error("Branchen konnten nicht gespeichert werden", e);
      // Gleicher Weg wie bei den Menschen fürs Unternehmen: melden, aber die
      // fertige Anlage nicht kippen.
      await hinweisen({
        titel: "Branchen nicht gespeichert",
        text: "Die Firma ist angelegt, aber die Branchen konnten nicht gespeichert werden. Bitte in der Akte nachtragen.",
        ton: "danger",
      });
    }
  };

  /** Kategorie und Marken des Gründungsdokuments — beim Verein die Satzung. */
  const gruendungsdokumentAblage = (): { category?: string; tags: string[] } =>
    istVerein
      ? { category: "Satzung", tags: ["satzung", "gesellschaftsvertrag"] }
      : { category: koerperschaft.hatRegister ? "Handelsregister" : undefined, tags: ["gesellschaftsvertrag"] };

  /**
   * Gründungsdokument gewählt. Verein im Bearbeiten-Modus der Akte: die
   * Satzung wird sofort abgelegt, wie vorher über den eigenen Knopf unter dem
   * Satzungszweck. Sonst vorgemerkt und nach dem Speichern abgelegt.
   */
  const waehleGruendungsdokument = (datei: File | null) => {
    if (datei && bearbeiten && istVerein) {
      void documentService
        .upload({ file: datei, contactId: bearbeiten.contactId, ...gruendungsdokumentAblage() })
        .then(() => {
          setVertragDatei(datei);
          setVertragAbgelegt(true);
        })
        .catch((err) =>
          void hinweisen({
            titel: "Satzung nicht hochgeladen",
            text: err instanceof Error ? err.message : String(err),
            ton: "danger",
          }),
        );
      return;
    }
    setVertragAbgelegt(false);
    setVertragDatei(datei);
  };

  /**
   * Gesellschaftsvertrag bzw. Satzung ablegen. Die eigene Datei trägt dieselbe
   * Marke wie der Registerabruf (`gesellschaftsvertrag`), damit „Gesellschafts-
   * vertrag auswerten" in der Akte sie findet. Der Registerabruf läuft im
   * Hintergrund weiter — Portal und Texterkennung brauchen Zeit, und die
   * fertige Anlage soll darauf nicht warten. Fehler kippen die Anlage nicht.
   */
  const legeGruendungsdokumentAb = async (contactId: number): Promise<void> => {
    // Verein: die Satzung liegt als Kategorie „Satzung" ab (so findet man sie
    // in der Akte), trägt aber AUCH die Marke des Gesellschaftsvertrags — damit
    // „Gesellschaftsvertrag auswerten" sie findet. Im Bearbeiten-Modus ist sie
    // schon beim Auswählen abgelegt worden (`vertragAbgelegt`).
    if (vertragDatei && !vertragAbgelegt) {
      try {
        await documentService.upload({ file: vertragDatei, contactId, ...gruendungsdokumentAblage() });
      } catch (err) {
        await hinweisen({
          titel: "Dokument nicht abgelegt",
          text:
            `${istVerein ? "Der Verein" : "Die Firma"} ist gespeichert, „${vertragDatei.name}" konnte aber nicht abgelegt werden.\n\n` +
            (err instanceof Error ? err.message : String(err)),
          ton: "danger",
        });
      }
    }
    if (vertragAusRegister) {
      void gesellschaftsvertragAuswerten(contactId).catch((err) =>
        console.warn("Gesellschaftsvertrag aus dem Register nicht geholt", err),
      );
    }
  };

  const partnerAnsprechpartnerRef = React.useRef<PartnerAnsprechpartnerListeRef>(null);
  const partnerKartenRef = React.useRef<PartnerVertragskartenRef>(null);
  const [partnerKontaktId, setPartnerKontaktId] = React.useState<number | null>(null);
  const partnerNachlauf = async (id: number): Promise<boolean> => {
    const probleme = [...(await partnerKartenRef.current?.speichern(id) ?? []), ...(await partnerAnsprechpartnerRef.current?.speichern(id) ?? [])];
    if (!probleme.length) return true;
    await hinweisen({titel: "Kontakt gespeichert – Partnerdaten unvollständig", text: probleme.join("\n") + "\nBitte erneut speichern. Fehlgeschlagene Eingaben bleiben erhalten.", ton: "danger"});
    return false;
  };

  const runSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (partnerKontaktId) {
        if (await partnerNachlauf(partnerKontaktId)) onSaveSuccess(partnerKontaktId);
        return;
      }
      const result = await onSave();
      if (typeof result !== "number") return;
      await uploadPendingPhoto(result);
      await persistRelations(result, !!convert);
      // Wer den Kontakt gebracht hat: Beziehung zum Empfehlungsgeber/Tippgeber
      // und auf Wunsch dessen Status. Scheitert etwas, bleibt der Kontakt
      // angelegt — der Hinweis sagt, was nachzutragen ist.
      if (!convert) {
        const probleme = await herkunftVerknuepfen(result, formFields, contactOrigins);
        if (probleme.length) {
          await hinweisen({
            titel: "Kontakt gespeichert – bitte nachtragen",
            text: "Der Kontakt ist gespeichert, aber:\n\n" + probleme.join("\n"),
            ton: "danger",
          });
        }
      }
      await uebernehmeRegisterPersonen(result);
      await legeRegisterAbdruckAb(result);
      await speichereWeitereBranchen(result);
      await legeGruendungsdokumentAb(result);
      for (const ef of pendingEinladungFilesRef.current) {
        try {
          await documentService.upload({ file: ef, contactId: result, category: "Einladung" });
        } catch (docErr) {
          console.error("[Anlage] Einladung konnte nicht als Dokument abgelegt werden:", docErr);
        }
      }
      if (!isProduktpartner) await vollmachtenNachAnlageMelden(result, vollmachten);
      // Firma als Interessent + NKG-Feld auf „Ja" → NKG-Vorgang anlegen (kein Popup).
      if (isInteressent && nkgWanted) {
        try {
          await createNkgVorgang(result);
        } catch {
          /* NKG-Vorgang optional — Anlage nicht blockieren */
        }
      }
      // „Analysemodus starten": erst speichern, dann direkt in die interne
      // Berater-Risikoanalyse (risikoanalyse-step) — derselbe Weg wie
      // „Beratermodus" im Start-Popup der Akte (User 28.09.2026), ohne Link.
      if (analyseNachSpeichernRef.current) {
        analyseNachSpeichernRef.current = false;
        onSaveSuccess(result);
        navigate(`/contacts/${result}/risikoanalyse-step`);
        return;
      }
      if (isProduktpartner) {
        setPartnerKontaktId(result);
        if (!await partnerNachlauf(result)) return;
      }
      onSaveSuccess(result);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Bearbeiten-Modus: die Nachläufe, die sonst `runSubmit` erledigt —
  // Personen aus dem Handelsregister, Gründungsdokument, Einladungsdateien.
  // Die Akte ruft das nach ihrem Speichern auf (utils/anlageNachlauf.ts).
  if (bearbeiten?.nachlauf) {
    bearbeiten.nachlauf.current = {
      geaendert: () => (partnerKartenRef.current?.geaendert() ?? false) || (partnerAnsprechpartnerRef.current?.geaendert() ?? false),
      ausfuehren: async (id) => {
        const probleme: string[] = [];
        // Beide melden ihre Fehler selbst (alert), wie in der Neuanlage.
        await uebernehmeRegisterPersonen(id);
        setRegisterStandVersion((v) => v + 1);
        await legeGruendungsdokumentAb(id);
        for (const ef of pendingEinladungFilesRef.current) {
          try {
            await documentService.upload({ file: ef, contactId: id, category: "Einladung" });
          } catch {
            probleme.push(`Die Einladung „${ef.name}" konnte nicht abgelegt werden.`);
          }
        }
        pendingEinladungFilesRef.current = [];
        if (isProduktpartner && partnerKartenRef.current) {
          const partnerProbleme = [...await partnerKartenRef.current.speichern(id), ...(await partnerAnsprechpartnerRef.current?.speichern(id) ?? [])];
          if (partnerProbleme.length) throw new Error(partnerProbleme.join("\n"));
        }
        return probleme;
      },
    };
  }

  const handleSubmit = async () => {
    if (isSubmitting) return;
    // Eine aus einer Schnellanlage geöffnete Vollanlage ist noch offen: Ihr
    // Kontakt stünde hier noch als Schnellanlage und entstünde doppelt.
    if (await speichernWegenVollanlageBlockiert(offeneVollanlagen)) return;
    // Bearbeiten-Modus: Der Aufrufer speichert (Diff-PUT, Branchen,
    // Beziehungsabgleich). Die Nachläufe der Anlage gehören nicht hierher.
    if (bearbeiten) {
      setIsSubmitting(true);
      try {
        const id = await onSave();
        if (id !== false) onSaveSuccess(id);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    // Beim Umwandeln ist die Maske vorausgefüllt: Was hier fehlt, wird gelöscht.
    // Geprüft wird nur der Bestand, den der Abgleich verantwortet — die Rollen
    // der Menschen fürs Unternehmen ersetzt ein eigener Endpoint.
    if (convert) {
      const removed = describeRemovedRelations(
        { firmaConn: parseArr(formFields.rel_firmaConn) },
        syncPreviousIds(),
        parseLabels(formFields.rel_previousLabels),
      );
      if (removed.length) {
        setRemovalPrompt(removed);
        return;
      }
    }
    await runSubmit();
  };

  // ── Klapp-Rahmen ──────────────────────────────────────────────────────
  const begriffKarten = useUnternehmensBegriff(Number(formFields.legalFormId) || undefined);
  const kurzfassungen: Record<string, React.ReactNode> = {
    [koerperschaft.anschriftTitel]: anschriftKurz(f("street"), f("postalcode"), f("city"), extraAddresses.length),
    Kommunikation: kommunikationKurz(
      [f("phone"), f("mobile"), ...weitereWege.filter((w) => w.type !== "email").map((w) => w.value)],
      [f("email"), ...weitereWege.filter((w) => w.type === "email").map((w) => w.value)],
    ),
    Bank: bankKurz(f("iban"), f("bankname"), extraBanks.length),
    [verantwortungTitel(begriffKarten)]: anzahlKurz(parseArr(formFields.rel_menschen).length, "Person", "Personen"),
    "Verbindungen zu Firmen": anzahlKurz(parseArr(formFields.rel_firmaConn).length, "Firma", "Firmen"),
  };
  // In der Akte starten leere Karten zugeklappt (User 30.09.2026); bei der
  // Neuanlage bleibt alles offen. Nur der erste Stand zählt (useKartenKlapp).
  const klapp = useKartenKlapp({
    kurzfassungen,
    fehlt: { "Allgemeine Informationen": !requiredOk, Verantwortlichkeit: !foOrBoOk },
    startZu: bearbeiten
      ? leereKarten(
          formFields,
          firmaKarten({ anschrift: koerperschaft.anschriftTitel, verantwortung: verantwortungTitel(begriffKarten) }),
          kurzfassungen,
        )
      : undefined,
  });
  const speichern = () => void handleSubmit();

  // „Analysemodus starten" neben Speichern (User 27.09.2026, nur Firma).
  const analyseNachSpeichernRef = React.useRef(false);
  const speichernUndAnalyse = () => {
    analyseNachSpeichernRef.current = true;
    void handleSubmit().finally(() => {
      // Ist das Speichern gescheitert, gilt der Wunsch nicht für den nächsten Klick.
      analyseNachSpeichernRef.current = false;
    });
  };

  // Was noch fehlt — für Warndreieck und Popup der Speichern-Leiste.
  const maengel: AnlageMangel[] = [
    ...(!requiredOk
      ? [{
          hinweis: "Bitte Pflichtfelder (*) ausfüllen",
          felder: [
            ...(!f("name").trim() ? ["Firmenname"] : []),
            ...(!isProduktpartner && !f("gesellschaftsform").trim() ? ["Rechtsform"] : []),
          ],
        }]
      : []),
    ...(!foOrBoOk
      ? [{ hinweis: "Bitte Frontoffice (FO) oder Backoffice (BO) wählen", felder: ["Verantwortlichkeit: FO oder BO"] }]
      : []),
    // Im Bearbeiten-Modus stammen Formatfehler oft aus dem Bestand — Hinweis,
    // aber keine Sperre (sonst ließe sich die Akte nicht mehr speichern).
    ...(!formatValid
      ? [{ hinweis: "Bitte markierte Felder korrigieren", sperrt: !bearbeiten }]
      : []),
  ];

  // ── Weitere Anschriften / Banken ──────────────────────────────────────
  // Hinzufügen, Fertig und Entfernen erledigt die EintragsListe; hier landet
  // nur der fertige Stand — im lokalen State UND als JSON im Formular.
  const setzeWeitereAnschriften = (next: WeitereAnschrift[]) => {
    setExtraAddresses(next);
    syncJson("additionalAddresses", next);
  };
  const setzeWeitereBanken = (next: WeitereBank[]) => {
    setExtraBanks(next);
    syncJson("additionalBanks", next);
  };

  // ── Repeater Banken ───────────────────────────────────────────────────────
  // --- Bankdaten aus der IBAN ---------------------------------------------
  // Sobald die IBAN eine deutsche Bankleitzahl trägt, stehen BIC und
  // Kreditinstitut fest. Eingetragen wird nur in LEERE Felder — eine bewusst
  // abweichende Eingabe (etwa nach einer Bankfusion) darf nicht stillschweigend
  // korrigiert werden. Dasselbe Muster nutzt schon die Steuernummer, die das
  // Finanzamt vorbelegt.
  const bankUebernehmen = (bank: BankTreffer) => {
    const leer = (v: unknown) => !String(v ?? "").trim();
    if (bank.bic && leer(f("bic"))) set("bic", bank.bic);
    if (leer(f("bankname"))) set("bankname", bank.bankName);
  };


  const rufnummernFuerModal: ErreichbarkeitRufnummerItem[] = [];

  if (f("phone")?.trim()) {
    rufnummernFuerModal.push({
      id: "phone",
      label: isProduktpartner ? "Telefon (Zentrale)" : "Telefon",
      number: f("phone"),
      erreichbarkeit: erreichbarkeiten["phone"] || "",
      onChange: (v) => setErreichbarkeitField("phone", v),
    });
  }
  if (f("mobile")?.trim()) {
    rufnummernFuerModal.push({
      id: "mobile",
      label: "Mobil",
      number: f("mobile"),
      erreichbarkeit: erreichbarkeiten["mobile"] || "",
      onChange: (v) => setErreichbarkeitField("mobile", v),
    });
  }
  extraCommunications
    .filter((c) => (c.type === "phone" || c.type === "mobile" || c.type === "fax") && c.value?.trim())
    .forEach((c) => {
      const baseType = c.type === "mobile" ? "Mobil" : c.type === "fax" ? "Fax" : "Telefon";
      const fullLabel = getPlainCommLabel(baseType, c.label);
      rufnummernFuerModal.push({
        id: c.id,
        label: fullLabel,
        number: c.value,
        erreichbarkeit: c.erreichbarkeit || "",
        onChange: (v) => updateExtraComm(c.id, { erreichbarkeit: v }),
      });
    });

  // Gesellschaftsvertrag bzw. Satzung. Beim Verein unter dem Satzungszweck,
  // sonst unter der Tätigkeitsbeschreibung — nie zweimal.
  const gruendungsdokumentFeld = (
    <GruendungsdokumentFeld
      istVerein={koerperschaft.art === "verein"}
      registerName={koerperschaft.registerLabel}
      registerMoeglich={!!f("handelsregisterNummer").trim() && !!f("registergericht").trim()}
      registerFehltHinweis={
        koerperschaft.hatRegister
          ? "Zum Abrufen erst Registernummer und Registergericht eintragen."
          : null
      }
      datei={vertragDatei}
      onDatei={waehleGruendungsdokument}
      abgelegt={vertragAbgelegt}
      ausRegister={vertragAusRegister}
      onAusRegister={setVertragAusRegister}
    />
  );

  // „+ Weitere Informationen erfassen" in den Schnellanlagen dieser Maske:
  // FO/BO und — im Bearbeiten-Modus der Akte — die Id dieser Firma gehen in
  // die Vollanlage mit (Unterkontakte erben ihre Verantwortung).
  const elternId = bearbeiten?.contactId ?? convert?.contactId;
  const vollanlageEltern = React.useMemo<VollanlageEltern>(
    () => ({
      elternId,
      elternName: (typeof formFields.name === "string" ? formFields.name.trim() : "") || undefined,
      ownership: ownershipAusFormular(formFields),
    }),
    [formFields, elternId],
  );

  return (
    <OffeneVollanlagenContext.Provider value={offeneVollanlagen}>
    <VollanlageElternContext.Provider value={vollanlageEltern}>
    <KartenKlappContext.Provider value={klapp}>
    <div className={s.page}>
      <div className={s.scroll}>
        <div className={mergeClasses(s.inner, bearbeiten && s.innerEingebettet)}>
          {!bearbeiten && (
            <h1 className={s.pageTitle}>
              {convert ? "Zu Firma umwandeln" : koerperschaft.seitenTitel}
            </h1>
          )}
          {convert && <ConvertBanner from={convert.from} relations={convert.relationsWithoutRole} fields={convert.fieldsToClear} />}
          {partnerKontaktId && <p role="status">Der Kontakt ist gespeichert. Bitte die noch offenen Partnerdaten erneut speichern; Stammdaten anschließend in der Akte bearbeiten.</p>}
          <fieldset disabled={!!partnerKontaktId} style={{display:"contents",border:0,padding:0,margin:0}}>
          {/* 1 — Kontaktstatus (User 26.09.2026): Status, bei „Sonstige" Bereich |
              Rolle, Firma | Privat — Folgefelder der Rollen inline. */}
          {!bearbeiten && (
          <SectionCard title="Kontaktstatus">
            <KontaktStatusSchritt
              f={f}
              set={set}
              typ="firma"
              onTyp={onTypWechsel ? (t) => t === "person" && onTypWechsel("person") : undefined}
              convert={!!convert}
              coIntern={hasPermission("cointern:read")}
              folgefelder={(folgen) =>
                folgen.includes("verein") ? (
                  // Gegenüber-Kasten wie bei den übrigen Rollen (02.10.2026).
                  <GegenueberKarte frage={folgeFrage("verbindung", gewaehlteRollen)}>
                    {/* Dieselben Einträge wie die Karte „Mitgliedschaften" unten. */}
                    <VereinMitgliedschaften value={vereine} onChange={onVereineChange} ohneFunktion />
                  </GegenueberKarte>
                ) : null
              }
            >
              <DuplicateWarningPanel results={dup.results} loading={dup.loading} />
            </KontaktStatusSchritt>
          </SectionCard>
          )}

          {/* 2 — Allgemeine Informationen (inkl. Kommunikation). Der Block heißt
              in der Kundenakte und in der Personen-Anlage genauso; die
              Überschrift darunter bleibt, weil sie der Rechtsform folgt
              (Firmen-/Vereins-/Gesellschaftsangaben). */}
          <SectionCard
            title="Allgemeine Informationen"
            heading={isProduktpartner ? "Angaben zur Gesellschaft" : koerperschaft.kartenUeberschrift}
          >
            {/* Visitenkarte zuerst: Sie füllt Name, Anschrift und Kontaktwege
                vor — dieselbe Rolle, die beim Produktpartner der
                GesellschaftPicker darüber spielt. Deshalb steht der Knopf am
                Anfang des Blocks und nicht bei der Kommunikation. */}
            {!convert && !bearbeiten && (
              <FieldRow columns={1}>
                <div>
                  <button
                    type="button"
                    className={s.fillBtn}
                    title="Vorder- und Rückseite abfotografieren; die erkannten Angaben werden vor der Übernahme angezeigt"
                    onClick={() => setScanOffen(true)}
                  >
                    <IconVisitenkarte /> Datei scannen zur vereinfachten Kontaktanlage
                  </button>
                </div>
              </FieldRow>
            )}
            <FieldRow columns={2}>
              {/* Sternchen und Leer-Meldung nur in der Anlage: im Umwandlungs-Modus
                  ist nichts pflichtig (bestehender Kontakt, kein Anlagevorgang). */}
              <LabeledInput
                label={isProduktpartner ? "Name der Gesellschaft" : koerperschaft.nameLabel}
                name="name"
                required={!convert}
                invalid={!convert && empty("name")}
                errorText="Name ist erforderlich."
                value={f("name")}
                onChange={(v) => {
                  set("name", v);
                  if (!isProduktpartner) applyLegalFormFromName(v);
                  // Kontoinhaber, der auf den bisherigen Namen lautet (gespeichert
                  // oder als Vorschlag), zieht mit — Umfirmierung. Geleert folgt
                  // er dem Vorschlag und wird beim Speichern neu geschrieben.
                  const inhaber = String(f("accountHolder") ?? "").trim();
                  if (inhaber && (inhaber === String(f("name") ?? "").trim() || inhaber === kontoinhaberName)) {
                    set("accountHolder", "");
                  }
                }}
                placeholder={isProduktpartner ? "Generali Deutschland Lebensversicherung AG" : koerperschaft.namePlatzhalter}
              />
              {isProduktpartner ? (
                <LabeledInput
                  label="Kurzform"
                  name="kurzname"
                  value={f("kurzname")}
                  onChange={(v) => set("kurzname", v)}
                  placeholder="GED Leben"
                  hint="Kurzbezeichnung für Listen und Verträge."
                />
              ) : (
                <LegalFormSelect
                  label="Rechtsform"
                  name="gesellschaftsform"
                  required={!convert}
                  value={f("gesellschaftsform")}
                  onChange={(v, id) => { legalFormTouchedRef.current = true; setLegalFormHinweis(null); set("gesellschaftsform", v); set("legalFormId", id ?? ""); }}
                  freitext={f("rechtsform")}
                  onFreitextChange={(v) => set("rechtsform", v)}
                  hinweis={legalFormHinweis}
                />
              )}
            </FieldRow>
            {/* Partnerkopf: Name, GDV-Gesellschaft, Partnerstatus. */}
            {isProduktpartner && (
              <FieldRow columns={1}>
                <GesellschaftPicker
                  value={f("gesellschaftId")}
                  onSelect={(g) => applyGesellschaft(g)}
                />
              </FieldRow>
            )}
            {isProduktpartner && <FieldRow columns={1}>
              <LabeledSelect label="Partnerstatus" name="partnerStatus"
                value={f("partnerStatus") || "aktiv"} onChange={v => set("partnerStatus", v)}
                options={["aktiv", "ruhend", "in Prüfung", "beendet"].map(value => ({ value, label: value }))} />
            </FieldRow>}
            {/* Website direkt unter den Namen — von hier aus wird gesucht: mit
                Website (oder nur dem Namen) holt „Daten befüllen" Anschrift,
                Telefon, E-Mail und Handelsregister aus dem Impressum. Deshalb
                steht das Feld oben und nicht unten bei der Kommunikation.
                Der Namenszusatz ist aus der Anlage raus (er wird nur für die im
                Altsystem abgeschnittenen Firmennamen gebraucht) — pflegbar
                bleibt er in der Detailansicht. */}
            <div className={s.websiteRow}>
              <LabeledInput
                label="Website"
                name="website"
                leftIcon={<IconGlobe />}
                value={f("website")}
                onChange={(v) => set("website", v)}
                placeholder="www.muster-gmbh.de"
              />
              {!isProduktpartner && (
                <button
                  type="button"
                  className={s.fillBtn}
                  disabled={befuellenGesperrt}
                  title={
                    befuellenGesperrt
                      ? "Bitte zuerst den Namen oder die Website eingeben"
                      : "Telefon, E-Mail, Handelsregister und Anschrift aus dem Impressum übernehmen"
                  }
                  onClick={() => setBefuellenOffen(true)}
                >
                  <IconGlobe /> Daten befüllen
                </button>
              )}
            </div>
            {/* Alles Folgende beschreibt ein KUNDEN-Unternehmen — Branche,
                Geschäftsjahr, Steuer, Handelsregister. Für eine Gesellschaft ist
                davon nichts relevant und nichts erfassbar. */}
            {!isProduktpartner && (
            <>
            {/* Vor der Branche: welche Aufgabe die Firma im Verbund übernimmt.
                Sie entscheidet, welche Risiken bei ihr überhaupt liegen — eine
                reine Besitzgesellschaft trägt Sachwerte, aber kein
                Betriebsrisiko. Mehrfachauswahl, weil beides zusammenfällt. */}
            {/* Beim Verein entfällt die Frage (User 28.09.2026) — ein Verein
                ist weder Betriebs- noch Besitzgesellschaft. */}
            {!istVerein && (
            <FieldRow columns={1}>
              <OptionChips
                label="Unternehmensfunktion"
                name="unternehmensfunktion"
                options={UNTERNEHMENSFUNKTIONEN}
                value={f("unternehmensfunktion")}
                onChange={(v) => set("unternehmensfunktion", v)}
                hint="Mehrfachauswahl — eine Firma kann Betrieb führen und zugleich Immobilien halten."
                hintUnterLabel
              />
            </FieldRow>
            )}
            {/* Beim Verein heißt das Feld „Satzungszweck" und landet in
                `Contact.satzungszweck` statt `jobOccupation`; die Branche steht
                dann DARUNTER (User 28.09.2026). */}
            {istVerein && (
              <FieldRow columns={1}>
                <LabeledTextarea
                  label="Satzungszweck"
                  name="satzungszweck"
                  value={f("satzungszweck")}
                  onChange={(v) => set("satzungszweck", v)}
                  rows={6}
                  placeholder="Wofür ist der Verein laut Satzung da? (z. B. Förderung des Amateursports)"
                />
              </FieldRow>
            )}
            {/* Satzung — direkt unter dem Satzungszweck (User 30.09.2026):
                hochladen oder aus dem Vereinsregister holen. Unter der Branche
                steht es beim Verein dann nicht mehr. */}
            {istVerein && gruendungsdokumentFeld}
            <FieldRow columns={1}>
              {/* Branche in zwei Ebenen aus dem Katalog „Branchenklassifizierung
                  NEU 2026 09 01": Branche gemäß KMVS → Branchengruppe.
                  Gespeichert wird die id der Gruppe (branchengruppeId); der
                  Anzeigepfad wird zusätzlich als Freitext in `industry`
                  (→ jobIndustry) gespiegelt, damit die Detailansicht die Branche
                  zeigt. Optional — nicht Pflicht.
                  Beliebig viele, alle gleichrangig — seit 28.09.2026 gibt es
                  keine „Hauptbranche" mehr in der Maske. Die vollständige Liste
                  geht über /contact/:id/branchengruppen (braucht die
                  Kontakt-id); die erste Zeile spiegelt sich weiter in die
                  Kontaktspalten, aus denen Listen und Suche lesen. */}
              <BrancheGruppenListe
                begriffe={koerperschaft.branche}
                value={brancheEintraege}
                onChange={(next) => {
                  setBrancheEintraege(next);
                  const haupt = next[0];
                  set(
                    "branchengruppeId",
                    haupt?.branchengruppeId != null ? String(haupt.branchengruppeId) : "",
                  );
                  set("brancheFreitext", haupt?.freitext ?? "");
                  // Der Freitext MUSS mit ins JSON — sonst ist er nach einem
                  // Fenster-Wechsel weg, ohne dass etwas fehlschlägt.
                  set(
                    "weitereBranchengruppen",
                    JSON.stringify(
                      next
                        .slice(1)
                        .filter((e) => e.branchengruppeId != null)
                        .map((e) => ({
                          branchengruppeId: e.branchengruppeId,
                          freitext: e.freitext,
                        })),
                    ),
                  );
                }}
                onHauptbrancheGruppe={(gruppe, freitext) =>
                  set("industry", gruppe ? formatGruppeMitFreitext(gruppe, freitext) : "")
                }
              />
            </FieldRow>
            {/* EIN Feld für den ganzen Betrieb (PO 17.08.2026) — vorher hing je
                Branche eine eigene Beschreibung, und wer Handel UND Werkstatt
                führte, schrieb dasselbe Geschäft zweimal auf. Landet als
                `jobOccupation` am Kontakt und heißt in der Akte genauso. */}
            {!istVerein && (
            <FieldRow columns={1}>
              <LabeledTextarea
                label={istBesitzgesellschaft(f("unternehmensfunktion")) ? "Was besitzt dieses Unternehmen?" : "Tätigkeitsbeschreibung"}
                name="taetigkeitsbeschreibung"
                value={f("taetigkeitsbeschreibung")}
                onChange={(v) => set("taetigkeitsbeschreibung", v)}
                rows={6}
                placeholder={
                  istBesitzgesellschaft(f("unternehmensfunktion"))
                    ? "z. B. Betriebsgrundstück Musterstraße 1, Lagerhalle, Maschinenpark (an die Betriebs-GmbH vermietet)"
                    : "Was macht der Betrieb konkret? (z. B. Reparatur von Baumaschinen, kein Neuwagenhandel)"
                }
                aufzaehlung
              />
            </FieldRow>
            )}
            {/* Unter der Tätigkeit: das Gründungsdokument, aus dem sie stammt.
                Beim Verein steht es unter dem Satzungszweck. */}
            {!istVerein && gruendungsdokumentFeld}
            {/* Gründung und Geschäftsjahr stehen nebeneinander: beides
                sind Stichtage aus der Gründungsurkunde, und beide standen
                vorher als halbleere Einzelzeilen untereinander. */}
            <FieldRow columns={2}>
              <LabeledInput label="Gründungsdatum" name="gruendungsdatum" type="date" value={f("gruendungsdatum")} onChange={(v) => set("gruendungsdatum", v)} />
              <div className={fs.field}>
                {/* Das Feld heißt seit dem 13.09.2026 schlicht „Geschäftsjahr"
                    (vorher „Geschäftsjahresbeginn", PO 19.08.2026). Inhaltlich
                    ist und bleibt es der BEGINN; gespeichert wird unverändert
                    „TT.MM" in Contact.financialYearEnd. Weder Spalte noch Wert
                    ändern sich — nur die Beschriftung. */}
                <label className={fs.label}>Geschäftsjahr</label>
                <div style={{ display: "flex", gap: 12 }}>
                  <select
                    className={mergeClasses(fs.input, fs.selectControl)}
                    style={{ flex: 1 }}
                    name="geschaeftsjahrTag"
                    value={f("geschaeftsjahrTag")}
                    onChange={(e) => set("geschaeftsjahrTag", e.target.value)}
                  >
                    <option value="">Tag</option>
                    {gjTagOptionen(f("geschaeftsjahrMonat"), f("geschaeftsjahrTag")).map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <select
                    className={mergeClasses(fs.input, fs.selectControl)}
                    style={{ flex: 1 }}
                    name="geschaeftsjahrMonat"
                    value={f("geschaeftsjahrMonat")}
                    onChange={(e) => {
                      const m = e.target.value;
                      set("geschaeftsjahrMonat", m);
                      // Tag ggf. auf Monats-Maximum kappen (nur wenn beide gesetzt).
                      const max = m ? gjDaysInMonth(m) : 31;
                      if (f("geschaeftsjahrTag") && (parseInt(f("geschaeftsjahrTag"), 10) || 1) > max) {
                        set("geschaeftsjahrTag", String(max).padStart(2, "0"));
                      }
                    }}
                  >
                    <option value="">Monat</option>
                    {GJ_MONTHS.map((m, i) => {
                      const v = String(i + 1).padStart(2, "0");
                      return <option key={v} value={v}>{m}</option>;
                    })}
                  </select>
                </div>
              </div>
            </FieldRow>
            {/* Rechtsvorgänger = die frühere Rechtsform (Katalog wie die
                Rechtsform, Contact.oldLegalForm). Steht bei der Gründung, weil
                beides aus dem chronologischen Abdruck kommt — der schlägt es
                dort vor (User 01.10.2026). */}
            <FieldRow columns={2}>
              <LabeledSelect
                label="Rechtsvorgänger (frühere Rechtsform)"
                name="oldLegalFormId"
                value={f("oldLegalFormId")}
                onChange={(v) => set("oldLegalFormId", v)}
                options={rechtsvorgaengerOptionen}
                placeholder="keine"
              />
              <div />
            </FieldRow>
            {/* Gemeinnützigkeit wie in der Akte (ContactDetailsRedesign), aber
                nur dort, wo die Rechtsform sie führt (fuehrtGemeinnuetzigkeit) —
                in EINER Zeile mit der Vorsteuer, beides Steuerstatus. */}
            <FieldRow columns={2}>
              <div className={s.field}>
                <span className={s.qLabel}>Vorsteuerabzugsberechtigt?</span>
                <div className={s.radioRow}>
                  {["Ja", "Nein"].map((opt) => (
                    <label key={opt} className={s.radioItem}>
                      <input type="radio" name="vorsteuerabzugsberechtigt" checked={f("vorsteuerabzugsberechtigt") === opt} onChange={() => set("vorsteuerabzugsberechtigt", opt)} />
                      {opt}
                    </label>
                  ))}
                </div>
              </div>
              {zeigeGemeinnuetzig ? (
                <LabeledSelect
                  label="Gemeinnützig (§§ 51–68 AO)"
                  name="istGemeinnuetzig"
                  value={f("istGemeinnuetzig")}
                  onChange={(v) => set("istGemeinnuetzig", v)}
                  options={[
                    { value: "Ja", label: "Ja" },
                    { value: "Nein", label: "Nein" },
                  ]}
                  placeholder="— wählen —"
                />
              ) : (
                <div />
              )}
            </FieldRow>
            {zeigeGemeinnuetzig && f("istGemeinnuetzig") === "Ja" && (
              <>
                <FieldRow columns={2}>
                  <LabeledInput
                    label="Freistellungsbescheid gültig bis"
                    name="freistellungsbescheidBis"
                    type="date"
                    value={f("freistellungsbescheidBis")}
                    onChange={(v) => set("freistellungsbescheidBis", v)}
                  />
                  <div />
                </FieldRow>
                {/* Zwecke als Liste über die volle Breite wie die Branchen
                    (User 01.10.2026) — dieselbe Komponente wie in der Akte.
                    Die CSV steht in Gesetzesreihenfolge; unbekannte Codes
                    bleiben erhalten. */}
                <FieldRow columns={1}>
                  <AoZweckeFeld
                    label="Steuerbegünstigte Zwecke (§§ 52–54 AO)"
                    value={f("zweckNrAO")}
                    onChange={(csv) => set("zweckNrAO", csv ?? "")}
                    className=""
                    idBase="anlage-zweck"
                  />
                </FieldRow>
              </>
            )}
            <FieldRow columns={2}>
              <SteuernummerInput label="Steuernummer" name="steuernummer" value={f("steuernummer")} onChange={(v) => set("steuernummer", v)} onFinanzamtResolved={(faName, bufa) => { if (!finanzamtTouchedRef.current) { set("finanzamt", faName); set("finanzamtBufa", bufa); } }} />
              <UstIdNrInput label="USt-IdNr." name="umsatzsteuernummer" value={f("umsatzsteuernummer")} onChange={(v) => set("umsatzsteuernummer", v)} />
            </FieldRow>
            {/* Beschriftung nach der gewählten Rechtsform — beim Verein
                „Vereinsregisternummer" / „VR 1234" (User 26.09.2026). */}
            <HandelsregisterInput
              numberLabel={koerperschaft.registerNummerLabel}
              numberPlaceholder={koerperschaft.registerPlatzhalter}
              amtsgerichtValue={f("registergericht")}
              numberValue={f("handelsregisterNummer")}
              onAmtsgerichtChange={(v) => set("registergericht", v)}
              onNumberChange={(v) => set("handelsregisterNummer", v)}
            />
            {/* Nur die drei Knöpfe, keine Texte (User 01.10.2026). Personen
                werden vorgemerkt und beim Speichern übernommen (`runSubmit`
                bzw. Nachlauf der Akte). */}
            <HandelsregisterKnoepfe
              contactId={bearbeiten?.contactId ?? null}
              nummer={f("handelsregisterNummer")}
              registergericht={f("registergericht")}
              eintraege={registerPersonen}
              onEintraege={setRegisterPersonen}
              onFelder={(werte) => werte.forEach((w) => set(w.feld, w.wert))}
              standVersion={registerStandVersion}
            />
            <FinanzamtSelect label="Finanzamt" name="finanzamt" value={f("finanzamt")} onChange={(v) => set("finanzamt", v)} onUserEdit={() => { finanzamtTouchedRef.current = true; }} onBufa={(bufa) => set("finanzamtBufa", bufa ?? "")} />
            </>
            )}
          </SectionCard>

          {/* Kommunikation als eigene SectionCard */}
          <SectionCard title="Kommunikation">
            {/* Erreichbarkeit oben rechts bevor Labels und Felder */}
            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "8px" }}>
              <button
                type="button"
                onClick={() => setErreichbarkeitModalOpen(true)}
                title={allgemeineErreichbarkeit ? `Erreichbarkeit: ${allgemeineErreichbarkeit}` : "Erreichbarkeit festlegen"}
                style={{
                  background: "none",
                  border: `1px solid ${allgemeineErreichbarkeit ? REFINED.aub : REFINED.line}`,
                  borderRadius: "8px",
                  padding: "5px 12px",
                  cursor: "pointer",
                  color: allgemeineErreichbarkeit ? REFINED.aub : REFINED.ink,
                  fontFamily: REFINED.fontUI,
                  fontSize: "0.82rem",
                  fontWeight: 600,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: allgemeineErreichbarkeit ? "rgba(58, 12, 49, 0.04)" : REFINED.white,
                }}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="8" cy="8" r="6.2" />
                  <polyline points="8 4.2 8 8 10.5 9.5" />
                </svg>
                <span>Erreichbarkeit</span>
              </button>
            </div>

            <FieldRow columns={2}>
              <div style={{ display: "flex", flexDirection: "column", rowGap: "16px", minWidth: 0 }}>
                {/* Telefone */}
                <PhoneInput
                  label={isProduktpartner ? "Telefon (Zentrale)" : "Telefon"}
                  name="phone"
                  geschaeftlich
                  kind="landline"
                  value={f("phone")}
                  onChange={(v) => set("phone", v)}
                  erreichbarkeit={erreichbarkeiten["phone"]}
                  onErreichbarkeitChange={(v) => setErreichbarkeitField("phone", v)}
                  unterFeld={wegHinweise("phone", "phone", "telefon")}
                />
                {extraCommunications
                  .filter((k) => k.type === "phone")
                  .map((k, idx) => {
                    const baseTitle = `Telefon ${idx + 2}`;
                    return (
                      <PhoneInput
                        key={k.id}
                        label={renderCommLabel(baseTitle, k.label)}
                        fieldLabelText={getPlainCommLabel(baseTitle, k.label)}
                        name={`extra-comm-${k.id}`}
                        geschaeftlich
                        zentrale={f("phone")}
                        kind="landline"
                        value={k.value}
                        onChange={(v) => updateExtraComm(k.id, { value: v })}
                        onRemoveIfEmpty={() => removeExtraComm(k.id)}
                        unterFeld={wegHinweise(`extra-comm-${k.id}`, k.type as WegTyp)}
                        erreichbarkeit={k.erreichbarkeit}
                        onErreichbarkeitChange={(v) => updateExtraComm(k.id, { erreichbarkeit: v })}
                      />
                    );
                  })}

                {/* Mobiltelefone */}
                <PhoneInput
                  label="Mobil"
                  name="mobile"
                  geschaeftlich
                  kind="mobile"
                  value={f("mobile")}
                  onChange={(v) => set("mobile", v)}
                  erreichbarkeit={erreichbarkeiten["mobile"]}
                  onErreichbarkeitChange={(v) => setErreichbarkeitField("mobile", v)}
                  unterFeld={wegHinweise("mobile", "mobile", "mobil")}
                />
                {extraCommunications
                  .filter((k) => k.type === "mobile")
                  .map((k, idx) => {
                    const baseTitle = `Mobil ${idx + 2}`;
                    return (
                      <PhoneInput
                        key={k.id}
                        label={renderCommLabel(baseTitle, k.label)}
                        fieldLabelText={getPlainCommLabel(baseTitle, k.label)}
                        name={`extra-comm-${k.id}`}
                        geschaeftlich
                        kind="mobile"
                        value={k.value}
                        onChange={(v) => updateExtraComm(k.id, { value: v })}
                        onRemoveIfEmpty={() => removeExtraComm(k.id)}
                        unterFeld={wegHinweise(`extra-comm-${k.id}`, k.type as WegTyp)}
                        erreichbarkeit={k.erreichbarkeit}
                        onErreichbarkeitChange={(v) => updateExtraComm(k.id, { erreichbarkeit: v })}
                      />
                    );
                  })}
              </div>

              <div style={{ display: "flex", flexDirection: "column", rowGap: "16px", minWidth: 0 }}>
                {/* E-Mail-Adressen */}
                <EmailInput
                  label={isProduktpartner ? "E-Mail (Zentrale)" : "E-Mail"}
                  name="email"
                  contactId={eigeneKontaktId}
                  value={f("email")}
                  onChange={(v) => set("email", v)}
                  unterFeld={wegHinweise("email", "email")}
                />
                {extraCommunications
                  .filter((k) => k.type === "email")
                  .map((k, idx) => {
                    const baseTitle = `E-Mail ${idx + 2}`;
                    return (
                      <EmailInput
                        key={k.id}
                        label={renderCommLabel(baseTitle, k.label)}
                        name={`extra-comm-${k.id}`}
                        contactId={eigeneKontaktId}
                        value={k.value}
                        onChange={(v) => updateExtraComm(k.id, { value: v })}
                        onRemoveIfEmpty={() => removeExtraComm(k.id)}
                        unterFeld={wegHinweise(`extra-comm-${k.id}`, k.type as WegTyp)}
                      />
                    );
                  })}

                {/* Fax */}
                <PhoneInput
                  label="Fax"
                  name="fax"
                  geschaeftlich
                  zentrale={f("phone")}
                  kind="fax"
                  value={f("fax")}
                  onChange={(v) => set("fax", v)}
                  unterFeld={wegHinweise("fax", "fax")}
                />
                {extraCommunications
                  .filter((k) => k.type === "fax")
                  .map((k, idx) => {
                    const baseTitle = `Fax ${idx + 2}`;
                    return (
                      <PhoneInput
                        key={k.id}
                        label={renderCommLabel(baseTitle, k.label)}
                        fieldLabelText={getPlainCommLabel(baseTitle, k.label)}
                        name={`extra-comm-${k.id}`}
                        geschaeftlich
                        zentrale={f("phone")}
                        kind="fax"
                        value={k.value}
                        onChange={(v) => updateExtraComm(k.id, { value: v })}
                        onRemoveIfEmpty={() => removeExtraComm(k.id)}
                        unterFeld={wegHinweise(`extra-comm-${k.id}`, k.type as WegTyp)}
                      />
                    );
                  })}
              </div>
            </FieldRow>

            <div className={s.rowActions}>
              <button
                type="button"
                className={s.ghostBtn}
                onClick={() => setWeitereKommOpen(true)}
              >
                + Weiterer Kommunikationsweg
              </button>
            </div>
          </SectionCard>


          {/* 3 — Herkunft */}
          {/* Herkunft gehört zur Anlage — beim Umwandeln eines bestehenden
              Kontakts ist die Frage sinnlos, und bei einem Produktpartner gibt
              es keine „Herkunft": er wird nicht gewonnen, sondern gelistet. */}
          {!convert && !isProduktpartner && (
            <SectionCard title="Wie kommt der Kontakt zustande?">
              <HerkunftAbschnitt
                isInteressent={isInteressent}
                auswahl={contactOrigins}
                toggle={toggleContactOrigin}
                sonstiges={contactOriginOther}
                onSonstiges={setContactOriginOtherValue}
                formFields={formFields}
                set={set}
              />
            </SectionCard>
          )}

          {/* 4 — Verantwortlichkeit */}
          {!bearbeiten && (
          <SectionCard title="Verantwortlichkeit">
            <OwnershipSection formFields={formFields} updateField={updateField} />
            {!foOrBoOk && (
              <div className={s.reqHint} style={{ marginTop: 8 }}>
                Pflicht: Frontoffice (FO) oder Backoffice-Team (BO) auswählen.
              </div>
            )}
          </SectionCard>
          )}

          {/* 5 — Anschrift des Sitzes (beim Verein/bei der Stiftung ohne
              „Unternehmen" im Titel) */}
          <SectionCard title={koerperschaft.anschriftTitel}>
            {/* Das Land steht VOR der Adresse: es entscheidet, wie der Rest zu
                lesen ist (PLZ-Format, Reihenfolge). Default Deutschland. */}
            <FieldRow columns={2}>
              <CountrySelect value={f("country")} onChange={(v) => set("country", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <AddressAutocomplete label="Straße und Hausnummer" name="street" value={f("street")} onChange={(v) => set("street", v)} land={f("country")} rightSlot={<KartenKnopf anschrift={{ strasse: f("street"), plz: f("postalcode"), ort: f("city"), land: f("country") }} />} onResolved={(a) => { set("street", a.street); set("postalcode", a.postalcode); set("city", a.city); set("cityArea", a.cityArea); }} />
              <LabeledInput label="Postleitzahl" name="postalcode" value={f("postalcode")} onChange={(v) => setPlzOderOrt("postalcode", v)}
                invalid={!plzPruefen(f("postalcode"), f("country")).ok}
                errorText={plzPruefen(f("postalcode"), f("country")).meldung}
                placeholder={plzPruefen("", f("country")).beispiel} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Ort" name="city" value={f("city")} onChange={(v) => setPlzOderOrt("city", v)} placeholder={PLACEHOLDERS.city} />
              <LabeledInput label="Ortsteil" name="cityArea" value={f("cityArea")} onChange={(v) => set("cityArea", v)} />
            </FieldRow>
            <EintragsListe<WeitereAnschrift>
              items={extraAddresses}
              onChange={setzeWeitereAnschriften}
              neu={neueAnschrift}
              hinzufuegenLabel="Weitere Anschrift"
              istLeer={anschriftIstLeer}
              pruefen={anschriftPruefen}
              renderFelder={(a, update, { index, entwurf }) => (
                <WeitereAnschriftFelder
                  wert={a}
                  onChange={update}
                  schluessel={entwurf ? "neu" : String(index)}
                  kopf="bezeichnung"
                />
              )}
            />
          </SectionCard>

          </fieldset>
          {isProduktpartner && <PartnerVertragskarten ref={partnerKartenRef} contactId={bearbeiten?.contactId} nurLesen={bearbeiten?.nurLesen || bearbeiten?.gesperrt} />}
          {isProduktpartner && <SectionCard title="Ansprechpartner"><PartnerAnsprechpartnerListe ref={partnerAnsprechpartnerRef} partnerContactId={bearbeiten?.contactId} partnerName={f("name")} partnerArt={gewaehlteRollen[0] === "Kooperationspartner" ? "Kooperationspartner" : "Produktpartner"} nurLesen={bearbeiten?.nurLesen || bearbeiten?.gesperrt || isSubmitting} /></SectionCard>}
          <fieldset disabled={!!partnerKontaktId} style={{display:"contents",border:0,padding:0,margin:0}}>

          {isProduktpartner && <PartnerAbrechnung kooperation={gewaehlteRollen[0] === "Kooperationspartner"} wert={f("partnerAbrechnungDirekt")} onChange={v => set("partnerAbrechnungDirekt", v)} onPortfolio={bearbeiten?.onPortfolio} />}
          {/* Ausblenden erhält den Formularbestand und alle Bank-IDs. */}
          {(!isProduktpartner || zeigtPartnerBank(gewaehlteRollen[0] === "Kooperationspartner", f("partnerAbrechnungDirekt"))) && (
          <SectionCard title="Bank">
            <FieldRow columns={2}>
              <LabeledInput label="Kontoinhaber" name="accountHolder" value={f("accountHolder") || kontoinhaberName} onChange={(v) => set("accountHolder", v)} placeholder="Name des Kontoinhabers" />
              <IbanInput label="IBAN" name="iban" value={f("iban")} onChange={(v) => set("iban", v)} onBankErkannt={bankUebernehmen} />
            </FieldRow>
            <FieldRow columns={2}>
              <BicInput label="BIC" name="bic" value={f("bic")} onChange={(v) => set("bic", v)} />
              <LabeledInput label="Kreditinstitut" name="bankname" value={f("bankname")} onChange={(v) => set("bankname", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <LabeledInput label="Bezeichnung" name="bankbezeichnung" value={f("bankbezeichnung")} onChange={(v) => set("bankbezeichnung", v)} placeholder="z. B. Geschäftskonto" />
              <div />
            </FieldRow>
            <EintragsListe<WeitereBank>
              items={extraBanks}
              onChange={setzeWeitereBanken}
              neu={neueBank}
              hinzufuegenLabel="Weitere Bankverbindung"
              istLeer={bankIstLeer}
              pruefen={bankPruefen}
              renderFelder={(b, update, { index, entwurf }) => (
                <WeitereBankFelder wert={b} onChange={update} schluessel={entwurf ? "neu" : String(index)} kontoinhaberVorschlag={kontoinhaberName} />
              )}
            />
          </SectionCard>
          )}

          {/* 7 — Social Media */}
          <SectionCard title="Social Media" collapsible defaultCollapsed={true}>
            <FieldRow columns={2}>
              <SocialMediaInput label="LinkedIn" name="linkedin" platform="linkedin" value={f("linkedin")} onChange={(v) => set("linkedin", v)} />
              <SocialMediaInput label="Instagram" name="instagram" platform="instagram" value={f("instagram")} onChange={(v) => set("instagram", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <SocialMediaInput label="Facebook" name="facebook" platform="facebook" value={f("facebook")} onChange={(v) => set("facebook", v)} />
              <SocialMediaInput label="TikTok" name="tiktok" platform="tiktok" value={f("tiktok")} onChange={(v) => set("tiktok", v)} />
            </FieldRow>
            <FieldRow columns={2}>
              <SocialMediaInput label="XING" name="xing" platform="xing" value={f("xing")} onChange={(v) => set("xing", v)} />
              <SocialMediaInput label="YouTube" name="youtube" platform="youtube" value={f("youtube")} onChange={(v) => set("youtube", v)} />
            </FieldRow>
          </SectionCard>

          {/* 8 — Mitgliedschaften (Verbände des Kunden). Ein Verein wird hier
              nicht selbst Mitglied erfasst — die Karte entfällt (User 26.09.2026). */}
          {!isProduktpartner && koerperschaft.art !== "verein" && (
            <SectionCard title="Mitgliedschaften">
              <VereinMitgliedschaften
                value={vereine}
                onChange={onVereineChange}
                ohneFunktion
                vereinLabel="Verein/Verband/Interessensgemeinschaft"
              />
            </SectionCard>
          )}

          {/* 8a — Zuwendungen, Veranstaltungen & Newsletter — seit 27.09.2026
              auch bei der Firma, nach den Mitgliedschaften. */}
          {!isProduktpartner && (
            <ZuwendungenKarte
              f={f}
              set={set}
              mitEigenemNewsletter
              emailOptionen={newsletterEmailOptionen}
              uploadFile={async (file) => {
                const fd = new FormData();
                fd.append("file", file);
                const res = await fetch(apiUrl("upload/contact-authority"), { method: "POST", body: fd });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return (await res.json()) as { url: string; name?: string };
              }}
              onOpenDocument={(url) => {
                if (url) window.open(apiUrl(url.replace(/^\/+/, "")), "_blank", "noopener,noreferrer");
              }}
              onEinladungsDatei={(file) => pendingEinladungFilesRef.current.push(file)}
            />
          )}

          {/* 8b — Bemerkung: freie Notiz zur Firma, die beim Anlegen schon
              bekannt ist (Contact.remark, auch in der Detailansicht sichtbar). */}
          <SectionCard title="Bemerkung" collapsible defaultCollapsed={true}>
            <LabeledTextarea
              label="Bemerkung"
              name="remark"
              value={f("remark")}
              onChange={(v) => set("remark", v)}
              placeholder="Freie Notiz zu diesem Kontakt"
              rows={5}
              hint="Steht später in der Detailansicht."
            />
          </SectionCard>

          {/* 9 — Logo (im Bearbeiten-Modus im Kopf der Akte) */}
          {!bearbeiten && (
          <SectionCard title="Logo">
            <input id="comp-photo-input" type="file" accept="image/*" style={{ display: "none" }} onChange={onPhotoPick} />
            <button type="button" className={s.fileBtn} onClick={() => elementMitId("comp-photo-input")?.click()}>
              {photoName || "Logo hochladen"}
            </button>
          </SectionCard>
          )}

          {/* 10 — Verknüpfungen (inline: GF/Gesellschafter, Verantwortlichkeiten, Firmen, KV).
              Beim Produktpartner entfallen sie: seine Personen sind
              Ansprechpartner und werden an der Person gepflegt, nicht hier. */}
          {!isProduktpartner && (
            <CompanyRelationsSection formFields={formFields} updateField={updateField} firmaId={bearbeiten?.contactId} />
          )}

          {/* 10b — Vollmachten, direkt vor NKG. Beim Produktpartner entfällt sie
              wie die Verknüpfungen: er ist kein Kunde, der uns bevollmächtigt. */}
          {!isProduktpartner && !bearbeiten && (
            <VollmachtenKarte
              modus="anlage"
              value={vollmachten}
              onChange={setVollmachten}
              vorschauDaten={{
                anzeigeName: f("name"),
                firma: f("name"),
                anschrift: anschriftEinzeilig(f("street"), f("postalcode"), f("city")),
              }}
              sammel={{ verbund: verbundAusFormular(parseArr(formFields.rel_firmaConn) as FirmaConnItem[]) }}
            />
          )}

          {/* 11 — NKG (nur bei Interessent): Inline-ja/nein statt Browser-Popup */}
          {isInteressent && !bearbeiten && <NkgKarte wert={nkgWanted} onChange={setNkgWanted} />}
          </fieldset>
        </div>
      </div>

      {/* Im Bearbeiten-Modus der Akte speichert das Schloss im Aktenkopf —
          die Fußleiste entfällt (User 28.09.2026). */}
      {!bearbeiten && (
      <div className={s.footerBar}>
        {/* Ohne Pflichtfelder (Umwandlungs-Modus) hat die Fortschrittsleiste
            nichts anzuzeigen — dann bleibt sie weg statt 0/0 zu behaupten. */}
        {requiredChecks.length > 0 && (
          <RequiredFieldsProgress filled={requiredFilled} total={requiredChecks.length} />
        )}
        <div className={s.footer}>
          {/* „Daten befüllen" steht jetzt oben neben dem Internet-Feld — dort,
              wo die Angabe hingehört, aus der gesucht wird. */}
          <div className={s.footerLeft}>
            <button type="button" className={s.cancelBtn} onClick={onClose}>
              {bearbeiten ? "Änderungen verwerfen" : "Abbrechen"}
            </button>
          </div>
          <AnlageSpeichernLeiste
            maengel={maengel}
            isSubmitting={isSubmitting}
            onSpeichern={speichern}
            // Aus einer Schnellanlage geöffnet (`speichernText`) kein
            // Analysemodus: Er verließe die Seite, statt den Kontakt dorthin
            // zurückzugeben, wo die Schnellanlage stand.
            onAnalysemodus={isProduktpartner || convert || bearbeiten || speichernText ? undefined : speichernUndAnalyse}
            saveClassName={s.saveBtn}
            speichernText={speichernText}
          />
        </div>
      </div>
      )}
      <WeitereKommunikationDialog
        open={weitereKommOpen}
        showZuordnung={false}
        geschaeftlich
        vorhandeneWege={kontaktWege}
        eigeneKontaktId={eigeneKontaktId}
        onClose={() => setWeitereKommOpen(false)}
        onSave={(entry) => {
          addExtraComm(entry);
          setWeitereKommOpen(false);
        }}
        onAdd={(entry) => {
          addExtraComm(entry);
          setWeitereKommOpen(false);
        }}
      />
      <ErreichbarkeitModal
        open={erreichbarkeitModalOpen}
        onClose={() => setErreichbarkeitModalOpen(false)}
        allgemeinValue={allgemeineErreichbarkeit}
        onAllgemeinChange={handleAllgemeinChange}
        rufnummern={rufnummernFuerModal}
      />

      <DateiScannenDialog
        open={scanOffen}
        ziel="firma"
        vorhanden={Object.fromEntries(
          Object.entries(formFields).map(([k, v]) => [k, str(v)]),
        )}
        gegenseiteMoeglich={!isProduktpartner}
        kontakt={
          // T-41: In der Akte (Bearbeiten/Umwandeln) startet „Aus KMVS wählen"
          // mit den Dokumenten dieses Kontakts.
          (bearbeiten?.contactId ?? convert?.contactId) != null
            ? { id: (bearbeiten?.contactId ?? convert?.contactId)!, name: str(formFields.name) || null }
            : null
        }
        onClose={() => setScanOffen(false)}
        onUebernehmen={applyDateiScan}
      />
      <FirmendatenBefuellenDialog
        open={befuellenOffen}
        eingabe={{
          name: String(f("name") ?? "").trim(),
          ort: f("city") || null,
          plz: f("postalcode") || null,
          strasse: f("street") || null,
          website: f("website") || null,
          email: f("email") || null,
        }}
        // Alles, was in dieser Maske schon getippt ist — der Firmenname
        // eingeschlossen: sonst hielte der Dialog ihn für leer, wählte den
        // Rechtsträger aus dem Impressum vor und ersetzte das Eingetippte ohne
        // Zutun. Die Feldnamen der Anlage sind die des Anreicherungs-Dienstes.
        vorhanden={vorhandeneAnlageWerte(f)}
        onClose={() => setBefuellenOffen(false)}
        onUebernehmen={applyImpressum}
        logo={logoFrage}
      />
      <ConfirmDialog
        open={!!removalPrompt}
        tone="danger"
        title="Verbindungen entfernen?"
        confirmLabel="Umwandeln und entfernen"
        cancelLabel="Zurück zur Maske"
        busy={isSubmitting}
        message={
          <>
            <div>Diese Verbindungen werden beim Speichern gelöscht:</div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
              {(removalPrompt ?? []).map((label, i) => (
                <li key={i}>{label}</li>
              ))}
            </ul>
          </>
        }
        onConfirm={() => {
          setRemovalPrompt(null);
          void runSubmit();
        }}
        onCancel={() => setRemovalPrompt(null)}
      />
    </div>
    </KartenKlappContext.Provider>
    </VollanlageElternContext.Provider>
    </OffeneVollanlagenContext.Provider>
  );
};

export default NewCompanyContactForm;
